import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  CardHeader,
  Chip,
  Collapse,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import StaffingEmployeeSelect from './StaffingEmployeeSelect';
import StaffingSalaryStructureTab from './StaffingSalaryStructureTab';
import StaffingPublicHolidaysTab from './StaffingPublicHolidaysTab';
import StaffingLeavePoliciesTab from './StaffingLeavePoliciesTab';
import StaffingHrPolicyTab from './StaffingHrPolicyTab';
import StaffingGrievancesTab from './StaffingGrievancesTab';
import {
  AccountTree as DepartmentsIcon,
  Assessment as ReportsIcon,
  AttachMoney as PayrollIcon,
  AccountBalanceWallet as SalaryStructureIcon,
  Business as BusinessIcon,
  CalendarMonth as HolidayIcon,
  Dashboard as DashboardIcon,
  Description as DocumentsIcon,
  EventNote as LeaveRequestsIcon,
  ReportProblem as GrievanceIcon,
  Folder as FolderIcon,
  Group as GroupIcon,
  Logout as LogoutIcon,
  Notifications as NotificationsIcon,
  PersonAdd as PersonAddIcon,
  Policy as PolicyIcon,
  Schedule as AttendanceIcon,
  Security as RolesIcon,
  Settings as SettingsIcon,
  Work as RecruitmentIcon,
  ArrowBack as ArrowBackIcon,
  ChevronRight as ChevronRightIcon,
  ContentCopy as ContentCopyIcon,
  Link as LinkIcon,
  Download as DownloadIcon,
  ExpandMore as ExpandMoreIcon,
  ManageAccounts as ManagersIcon,
  Search as SearchIcon,
  Groups as GroupsIcon,
  CheckCircleOutline as ActiveIcon,
  BeachAccess as LeaveIcon,
  PendingActions as PendingIcon,
  ArrowForward as ArrowForwardIcon,
  CalendarToday as CalendarTodayIcon
} from '@mui/icons-material';

const DASH = {
  bg: '#f1f5f9',
  card: '#ffffff',
  border: '#e2e8f0',
  text: '#0f172a',
  muted: '#64748b',
  accent: '#0f766e',
  accentSoft: '#f0fdfa'
};

const API_URL = process.env.REACT_APP_API_URL || '';

const navItems = [
  { id: 'overview', label: 'Dashboard', icon: <DashboardIcon fontSize="small" /> },
  { id: 'selectionCandidates', label: 'Selection Candidates', icon: <PersonAddIcon fontSize="small" /> },
  { id: 'employees', label: 'Employees', icon: <GroupIcon fontSize="small" /> },
  { id: 'managers', label: 'Managers', icon: <ManagersIcon fontSize="small" /> },
  { id: 'leavePolicies', label: 'Leave Policies', icon: <PolicyIcon fontSize="small" /> },
  { id: 'hrPolicy', label: 'HR Policy', icon: <PolicyIcon fontSize="small" /> },
  { id: 'leaveRequests', label: 'Leave Requests', icon: <LeaveRequestsIcon fontSize="small" /> },
  { id: 'grievances', label: 'Grievance Center', icon: <GrievanceIcon fontSize="small" /> },
  { id: 'attendance', label: 'Attendance', icon: <AttendanceIcon fontSize="small" /> },
  { id: 'publicHolidays', label: 'Public Holidays', icon: <HolidayIcon fontSize="small" /> },
  { id: 'salaryStructure', label: 'Salary Structure', icon: <SalaryStructureIcon fontSize="small" /> },
  { id: 'payroll', label: 'Payroll', icon: <PayrollIcon fontSize="small" /> },
  { id: 'documents', label: 'Documents', icon: <DocumentsIcon fontSize="small" /> }
];

const PUBLIC_APP_URL = process.env.REACT_APP_PUBLIC_URL || (typeof window !== 'undefined' ? window.location.origin : '');

function pct(part, whole) {
  if (!whole) return 0;
  return Math.min(100, Math.round((part / whole) * 1000) / 10);
}

function TeamHealthRing({ active, onLeave, inactive, size = 168 }) {
  const total = active + onLeave + inactive;
  const a = total ? (active / total) * 360 : 0;
  const b = total ? (onLeave / total) * 360 : 0;
  const degA = a;
  const degB = degA + b;
  const bg =
    total === 0
      ? '#e2e8f0'
      : `conic-gradient(#047857 0deg ${degA}deg, #b45309 ${degA}deg ${degB}deg, #94a3b8 ${degB}deg 360deg)`;

  return (
    <Box sx={{ width: size, height: size, borderRadius: '50%', background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: 'inset 0 0 0 12px #fff', mx: 'auto' }}>
      <Box sx={{ textAlign: 'center' }}>
        <Typography variant="h4" fontWeight={700} color={DASH.text} sx={{ lineHeight: 1 }}>
          {total}
        </Typography>
        <Typography variant="caption" color={DASH.muted} sx={{ fontSize: '0.7rem', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
          Headcount
        </Typography>
      </Box>
    </Box>
  );
}

function DashboardStatCard({ label, value, sublabel, icon, accent = DASH.accent, onClick }) {
  const inner = (
    <CardContent sx={{ p: 2.25, '&:last-child': { pb: 2.25 } }}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
        <Box>
          <Typography variant="caption" sx={{ color: DASH.muted, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', fontSize: '0.68rem' }}>
            {label}
          </Typography>
          <Typography variant="h4" sx={{ color: DASH.text, fontWeight: 700, mt: 0.75, lineHeight: 1.1 }}>
            {value ?? 0}
          </Typography>
          {sublabel && (
            <Typography variant="caption" sx={{ color: DASH.muted, display: 'block', mt: 0.5 }}>
              {sublabel}
            </Typography>
          )}
        </Box>
        <Box sx={{ width: 40, height: 40, borderRadius: 1, bgcolor: `${accent}14`, color: accent, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {icon}
        </Box>
      </Stack>
    </CardContent>
  );
  return (
    <Card
      elevation={0}
      sx={{
        border: `1px solid ${DASH.border}`,
        borderRadius: 1.5,
        bgcolor: DASH.card,
        transition: 'border-color 0.2s, box-shadow 0.2s',
        ...(onClick && { cursor: 'pointer', '&:hover': { borderColor: accent, boxShadow: '0 4px 20px rgba(15,23,42,0.06)' } })
      }}
    >
      {onClick ? <CardActionArea onClick={onClick}>{inner}</CardActionArea> : inner}
    </Card>
  );
}

function PlaceholderModule({ title, body }) {
  return (
    <Card sx={{ borderRadius: 3, boxShadow: '0 8px 24px rgba(15,23,42,0.06)' }}>
      <CardContent sx={{ py: 3 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
          <FolderIcon color="action" />
          <Typography variant="h6" fontWeight={700}>
            {title}
          </Typography>
        </Stack>
        <Alert severity="info" sx={{ mt: 1 }}>
          {body}
        </Alert>
      </CardContent>
    </Card>
  );
}

function StaffingCompanyDashboard({ onLogout }) {
  const user = useMemo(() => JSON.parse(localStorage.getItem('gogig_user') || '{}'), []);
  const token = localStorage.getItem('gogig_token');

  const [activeTab, setActiveTab] = useState('overview');
  const [error, setError] = useState('');
  const [overview, setOverview] = useState({
    totalEmployees: 0,
    activeEmployees: 0,
    employeesOnLeaveToday: 0,
    pendingLeaves: 0,
    openGrievances: 0,
    pendingLoiJoin: 0,
    holidays: 0
  });
  const [employees, setEmployees] = useState([]);
  const [managers, setManagers] = useState([]);
  const [managerForm, setManagerForm] = useState({ name: '', email: '', mobile: '', designation: '' });
  const [editingManagerId, setEditingManagerId] = useState(null);
  const [assigningManagerFor, setAssigningManagerFor] = useState(null);
  const [selectionCandidates, setSelectionCandidates] = useState([]);
  const [timelineByCandidate, setTimelineByCandidate] = useState({});
  const [candidateForm, setCandidateForm] = useState({ name: '', mobile: '', email: '', qualification: '', course: '' });
  const [joiningDateByCandidate, setJoiningDateByCandidate] = useState({});
  const [bulkFile, setBulkFile] = useState(null);
  const [payrollMonth, setPayrollMonth] = useState(new Date().getMonth() + 1);
  const [payrollYear, setPayrollYear] = useState(new Date().getFullYear());
  const [payrollRuns, setPayrollRuns] = useState([]);
  const [payrollPreview, setPayrollPreview] = useState(null);
  const [payrollPreviewLoading, setPayrollPreviewLoading] = useState(false);
  const [payrollActionLoading, setPayrollActionLoading] = useState(false);
  const [selectedRunId, setSelectedRunId] = useState(null);
  const [payslips, setPayslips] = useState([]);
  const [feedback, setFeedback] = useState('');
  const [salaryByCandidate, setSalaryByCandidate] = useState({});
  const [selectionDetailId, setSelectionDetailId] = useState(null);
  const [detailProfile, setDetailProfile] = useState(null);
  const [detailProfileLoading, setDetailProfileLoading] = useState(false);
  const [detailProfileError, setDetailProfileError] = useState('');
  const [inviteLinkDialog, setInviteLinkDialog] = useState({
    open: false,
    profileUrl: '',
    emailSent: false,
    emailSkipped: false,
    emailError: null
  });
  const [selfRegDialog, setSelfRegDialog] = useState({
    open: false,
    url: ''
  });
  const [selectionSearch, setSelectionSearch] = useState('');
  const [addCandidatesOpen, setAddCandidatesOpen] = useState(false);
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [outdoorRequests, setOutdoorRequests] = useState([]);
  const [requestStatusFilter, setRequestStatusFilter] = useState('pending');
  const [attendanceMonth, setAttendanceMonth] = useState(new Date().getMonth() + 1);
  const [attendanceYear, setAttendanceYear] = useState(new Date().getFullYear());
  const [attendanceRows, setAttendanceRows] = useState([]);
  const [documentsCandidateId, setDocumentsCandidateId] = useState('');
  const [documentsData, setDocumentsData] = useState(null);
  const [payslipZipMonth, setPayslipZipMonth] = useState(new Date().getMonth() + 1);
  const [payslipZipYear, setPayslipZipYear] = useState(new Date().getFullYear());
  const [payslipZipLoading, setPayslipZipLoading] = useState(false);
  const [attendanceBulkFile, setAttendanceBulkFile] = useState(null);

  const authHeaders = useMemo(
    () => ({ Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }),
    [token]
  );

  const apiRequest = useCallback(
    async (endpoint, options = {}) => {
      if (!API_URL) {
        throw new Error('REACT_APP_API_URL is not set. Add it to frontend/.env and restart the dev server.');
      }
      const res = await fetch(`${API_URL}${endpoint}`, {
        headers: authHeaders,
        ...options
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const msg = data.error || data.message || `Request failed (${res.status})`;
        throw new Error(msg);
      }
      return data;
    },
    [authHeaders]
  );

  const loadData = useCallback(async () => {
    setError('');
    try {
      const overviewData = await apiRequest('/api/company/hrms/overview');
      const stats = overviewData.stats || {};
      setOverview({
        totalEmployees: stats.totalEmployees ?? 0,
        activeEmployees: stats.activeEmployees ?? 0,
        employeesOnLeaveToday: stats.employeesOnLeaveToday ?? 0,
        pendingLeaves: stats.pendingLeaveRequests ?? stats.pendingLeaves ?? 0,
        openGrievances: stats.openGrievances ?? 0,
        pendingLoiJoin: stats.pendingLoiJoin ?? 0,
        holidays: stats.holidays ?? 0
      });
    } catch (e) {
      setError(
        e.message ||
          'HRMS API unavailable. Ensure backend exposes /api/company/hrms/* for staffing companies.'
      );
    }

    try {
      const empData = await apiRequest('/api/company/hrms/employees?include_all=1');
      setEmployees(empData.employees || []);
    } catch {
      setEmployees([]);
    }

    try {
      const mgrData = await apiRequest('/api/company/hrms/managers?includeInactive=1');
      setManagers(mgrData.managers || []);
    } catch {
      setManagers([]);
    }

    try {
      const sel = await apiRequest('/api/company/hrms/selection-candidates');
      setSelectionCandidates(sel.selectionCandidates || []);
    } catch {
      setSelectionCandidates([]);
    }
    try {
      const runs = await apiRequest('/api/company/hrms/payroll/runs');
      setPayrollRuns(runs.runs || []);
    } catch {
      setPayrollRuns([]);
    }

    try {
      const q = requestStatusFilter ? `?status=${requestStatusFilter}` : '';
      const lr = await apiRequest(`/api/company/hrms/leave-requests${q}`);
      setLeaveRequests(lr.requests || []);
      const od = await apiRequest(`/api/company/hrms/outdoor-duty-requests${q}`);
      setOutdoorRequests(od.requests || []);
    } catch {
      setLeaveRequests([]);
      setOutdoorRequests([]);
    }
  }, [apiRequest, requestStatusFilter]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const inactiveEmployees = Math.max(
    0,
    overview.totalEmployees - overview.activeEmployees - overview.employeesOnLeaveToday
  );

  const workforceRows = useMemo(
    () => [
      { label: 'Active workforce', value: overview.activeEmployees, color: '#047857' },
      { label: 'On leave today', value: overview.employeesOnLeaveToday, color: '#b45309' },
      { label: 'Awaiting activation', value: inactiveEmployees, color: '#64748b' },
      { label: 'Pending leave approvals', value: overview.pendingLeaves, color: '#6d28d9' },
      { label: 'Open grievances', value: overview.openGrievances ?? 0, color: '#b91c1c' }
    ],
    [overview, inactiveEmployees]
  );

  const maxWorkforce = useMemo(
    () => Math.max(1, ...workforceRows.map((r) => r.value), overview.totalEmployees),
    [workforceRows, overview.totalEmployees]
  );

  const activeRate = overview.totalEmployees
    ? Math.round((overview.activeEmployees / overview.totalEmployees) * 100)
    : 0;

  const pendingLeavePreview = useMemo(
    () => leaveRequests.filter((r) => r.status === 'pending').slice(0, 5),
    [leaveRequests]
  );

  const dashboardDateLabel = useMemo(
    () =>
      new Date().toLocaleDateString(undefined, {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      }),
    []
  );

  const activeEmployeesList = useMemo(() => employees.filter((e) => e.status === 'active'), [employees]);
  const pendingActivationList = useMemo(() => employees.filter((e) => e.status === 'loi_accepted'), [employees]);

  const attentionItems = useMemo(() => {
    const items = [];
    if (overview.pendingLeaves > 0) {
      items.push({ label: 'Leave requests awaiting approval', count: overview.pendingLeaves, tab: 'leaveRequests', severity: 'warning' });
    }
    if ((overview.openGrievances ?? 0) > 0) {
      items.push({ label: 'Open grievance cases', count: overview.openGrievances, tab: 'grievances', severity: 'error' });
    }
    if (overview.pendingLoiJoin > 0) {
      items.push({ label: 'Candidates pending LOI / join', count: overview.pendingLoiJoin, tab: 'selectionCandidates', severity: 'info' });
    }
    if (pendingActivationList.length > 0) {
      items.push({ label: 'Employees ready to activate', count: pendingActivationList.length, tab: 'employees', severity: 'info' });
    }
    return items;
  }, [overview, pendingActivationList.length]);

  const filteredSelectionCandidates = useMemo(() => {
    const q = selectionSearch.trim().toLowerCase();
    if (!q) return selectionCandidates;
    return selectionCandidates.filter((c) => {
      const haystack = [c.full_name, c.email, c.mobile, c.status, c.qualification, c.course]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [selectionCandidates, selectionSearch]);

  const formatCandidateStatus = (status) =>
    String(status || 'selection')
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (ch) => ch.toUpperCase());

  const fetchTimeline = useCallback(async (candidateId) => {
    try {
      const data = await apiRequest(`/api/company/hrms/candidates/${candidateId}/timeline`);
      setTimelineByCandidate((prev) => ({ ...prev, [candidateId]: data }));
    } catch {
      setTimelineByCandidate((prev) => ({ ...prev, [candidateId]: { letters: [], employment: null } }));
    }
  }, [apiRequest]);

  useEffect(() => {
    if (activeTab !== 'selectionCandidates') {
      setSelectionDetailId(null);
      setSelectionSearch('');
      setAddCandidatesOpen(false);
    }
  }, [activeTab]);

  useEffect(() => {
    if (!selectionDetailId || activeTab !== 'selectionCandidates') return undefined;
    let cancelled = false;
    (async () => {
      setDetailProfileLoading(true);
      setDetailProfileError('');
      try {
        const data = await apiRequest(`/api/company/hrms/candidates/${selectionDetailId}/profile`);
        if (!cancelled) setDetailProfile(data.profile);
      } catch (e) {
        if (!cancelled) {
          setDetailProfileError(e.message);
          setDetailProfile(null);
        }
      } finally {
        if (!cancelled) setDetailProfileLoading(false);
      }
      if (!cancelled) fetchTimeline(selectionDetailId);
    })();
    return () => {
      cancelled = true;
    };
  }, [selectionDetailId, activeTab, apiRequest, fetchTimeline]);

  const closeSelectionDetail = () => {
    setSelectionDetailId(null);
    setDetailProfile(null);
    setDetailProfileError('');
    setDetailProfileLoading(false);
  };

  const refreshSelectionDetail = useCallback(
    async (candidateIdOverride) => {
      const id = candidateIdOverride ?? selectionDetailId;
      if (!id) return;
      const shouldUpdateProfileState = !candidateIdOverride || id === selectionDetailId;
      if (shouldUpdateProfileState) {
        setDetailProfileLoading(true);
        setDetailProfileError('');
      }
      try {
        if (shouldUpdateProfileState) {
          const data = await apiRequest(`/api/company/hrms/candidates/${id}/profile`);
          setDetailProfile(data.profile);
        }
        await fetchTimeline(id);
        await loadData();
      } catch (e) {
        if (shouldUpdateProfileState) setDetailProfileError(e.message);
      } finally {
        if (shouldUpdateProfileState) setDetailProfileLoading(false);
      }
    },
    [selectionDetailId, apiRequest, fetchTimeline, loadData]
  );

  const activeManagers = useMemo(
    () => managers.filter((m) => m.status === 'active'),
    [managers]
  );

  const resetManagerForm = () => {
    setManagerForm({ name: '', email: '', mobile: '', designation: '' });
    setEditingManagerId(null);
  };

  const saveManager = async () => {
    try {
      setFeedback('');
      const payload = {
        name: managerForm.name.trim(),
        email: managerForm.email.trim() || null,
        mobile: managerForm.mobile.trim() || null,
        designation: managerForm.designation.trim() || null
      };
      if (!payload.name) {
        setFeedback('Manager name is required.');
        return;
      }
      if (editingManagerId) {
        await apiRequest(`/api/company/hrms/managers/${editingManagerId}`, {
          method: 'PATCH',
          body: JSON.stringify(payload)
        });
        setFeedback('Manager updated.');
      } else {
        await apiRequest('/api/company/hrms/managers', {
          method: 'POST',
          body: JSON.stringify(payload)
        });
        setFeedback('Manager added.');
      }
      resetManagerForm();
      await loadData();
    } catch (e) {
      setFeedback(e.message);
    }
  };

  const startEditManager = (m) => {
    setEditingManagerId(m.id);
    setManagerForm({
      name: m.name || '',
      email: m.email || '',
      mobile: m.mobile || '',
      designation: m.designation || ''
    });
  };

  const toggleManagerStatus = async (m) => {
    try {
      setFeedback('');
      const next = m.status === 'active' ? 'inactive' : 'active';
      await apiRequest(`/api/company/hrms/managers/${m.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ status: next })
      });
      setFeedback(next === 'active' ? 'Manager reactivated.' : 'Manager deactivated.');
      await loadData();
    } catch (e) {
      setFeedback(e.message);
    }
  };

  const assignEmployeeManager = async (candidateId, managerId) => {
    try {
      setAssigningManagerFor(candidateId);
      setFeedback('');
      await apiRequest(`/api/company/hrms/employees/${candidateId}/manager`, {
        method: 'PATCH',
        body: JSON.stringify({ manager_id: managerId || null })
      });
      setFeedback('Reporting manager updated.');
      await loadData();
    } catch (e) {
      setFeedback(e.message);
    } finally {
      setAssigningManagerFor(null);
    }
  };

  const addSingleCandidate = async () => {
    try {
      setFeedback('');
      await apiRequest('/api/company/hrms/selection-candidates', {
        method: 'POST',
        body: JSON.stringify(candidateForm)
      });
      setCandidateForm({ name: '', mobile: '', email: '', qualification: '', course: '' });
      setFeedback('Candidate added to selection list.');
      await loadData();
    } catch (e) {
      setFeedback(e.message);
    }
  };

  const uploadBulk = async () => {
    if (!bulkFile) return;
    try {
      setFeedback('');
      const form = new FormData();
      form.append('file', bulkFile);
      const res = await fetch(`${API_URL}/api/company/hrms/selection-candidates/bulk-upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: form
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || data.message || 'Bulk upload failed');
      setFeedback(`Bulk upload complete. Inserted: ${data.inserted}, Skipped: ${data.skipped}`);
      setBulkFile(null);
      await loadData();
    } catch (e) {
      setFeedback(e.message);
    }
  };

  const sendProfileInvite = async (candidateId) => {
    try {
      setFeedback('');
      const origin = typeof window !== 'undefined' ? window.location.origin : '';
      const data = await apiRequest(`/api/company/hrms/candidates/${candidateId}/send-profile-invite`, {
        method: 'POST',
        body: JSON.stringify({ frontend_origin: origin })
      });
      // Same pattern as supervisor "registration link": always use current app origin in the UI.
      const base = typeof window !== 'undefined' ? window.location.origin.replace(/\/$/, '') : '';
      const displayUrl =
        data.token && base ? `${base}/staffing-profile/${data.token}` : data.profileUrl || '';
      if (displayUrl) {
        setInviteLinkDialog({
          open: true,
          profileUrl: displayUrl,
          emailSent: !!data.emailSent,
          emailSkipped: !!data.emailSkipped,
          emailError: data.emailError || null
        });
      }
      const parts = [];
      if (data.emailSent) parts.push('Email sent to candidate with the same link.');
      else if (data.emailSkipped) parts.push('No email on file — copy the link from the dialog and share (WhatsApp, SMS, etc.).');
      else if (data.emailError) parts.push(`Email failed: ${data.emailError}`);
      setFeedback(parts.join(' ') || 'Profile link ready — share it with the candidate.');
      await loadData();
      await refreshSelectionDetail(candidateId);
    } catch (e) {
      setFeedback(e.message);
    }
  };

  const copyInviteLinkToClipboard = () => {
    const text = inviteLinkDialog.profileUrl;
    if (!text) return;
    navigator.clipboard.writeText(text).then(() => {
      setFeedback('Link copied to clipboard.');
    }).catch(() => {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setFeedback('Link copied to clipboard.');
    });
  };

  const fetchStaffingSelfRegistrationLink = async () => {
    try {
      setFeedback('');
      const data = await apiRequest('/api/company/hrms/staffing-self-registration-link');
      const base = typeof window !== 'undefined' ? window.location.origin.replace(/\/$/, '') : '';
      const url = data.token && base ? `${base}/staffing-register/${data.token}` : '';
      if (url) {
        setSelfRegDialog({ open: true, url });
      }
      setFeedback('Candidates can use this link to register themselves — no need to add them first.');
    } catch (e) {
      setFeedback(e.message);
    }
  };

  const copySelfRegLinkToClipboard = () => {
    const text = selfRegDialog.url;
    if (!text) return;
    navigator.clipboard.writeText(text).then(() => {
      setFeedback('Registration link copied to clipboard.');
    }).catch(() => {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setFeedback('Registration link copied to clipboard.');
    });
  };

  const downloadBulkTemplate = async () => {
    try {
      const res = await fetch(`${API_URL}/api/company/hrms/selection-candidates/template`, {
        method: 'GET',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || data.message || 'Failed to download template');
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'selection_candidates_template.csv';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setFeedback(e.message);
    }
  };

  const sendProvisional = async (candidateId) => {
    try {
      setFeedback('');
      const s = salaryByCandidate[candidateId] || {};
      const compensation = {
        basic_salary: Number(s.basic_salary) || 0,
        hra: Number(s.hra) || 0,
        conveyance: Number(s.conveyance) || 0,
        other_allowances: Number(s.other_allowances) || 0,
        gross_pay: Number(s.gross_pay) || 0,
        pf_employee: Number(s.pf_employee) || 0,
        pf_employer: Number(s.pf_employer) || 0,
        net_pay: Number(s.net_pay) || 0,
        ctc: Number(s.ctc) || 0
      };
      await apiRequest(`/api/company/hrms/candidates/${candidateId}/send-provisional`, {
        method: 'POST',
        body: JSON.stringify({
          designation: s.designation || 'Staff',
          location: s.location || 'N/A',
          compensation
        })
      });
      setFeedback('Provisional offer letter sent.');
      await loadData();
      await fetchTimeline(candidateId);
      await refreshSelectionDetail(candidateId);
    } catch (e) {
      setFeedback(e.message);
    }
  };

  const resendProvisional = async (candidateId) => {
    try {
      setFeedback('');
      const s = salaryByCandidate[candidateId] || {};
      const compensation = {
        basic_salary: Number(s.basic_salary) || 0,
        hra: Number(s.hra) || 0,
        conveyance: Number(s.conveyance) || 0,
        other_allowances: Number(s.other_allowances) || 0,
        gross_pay: Number(s.gross_pay) || 0,
        pf_employee: Number(s.pf_employee) || 0,
        pf_employer: Number(s.pf_employer) || 0,
        net_pay: Number(s.net_pay) || 0,
        ctc: Number(s.ctc) || 0
      };
      await apiRequest(`/api/company/hrms/candidates/${candidateId}/send-provisional`, {
        method: 'POST',
        body: JSON.stringify({
          designation: s.designation || 'Staff',
          location: s.location || 'N/A',
          compensation
        })
      });
      setFeedback('Provisional offer letter resent.');
      await loadData();
      await fetchTimeline(candidateId);
      await refreshSelectionDetail(candidateId);
    } catch (e) {
      setFeedback(e.message);
    }
  };

  const sendLoi = async (candidateId) => {
    const joining_date = joiningDateByCandidate[candidateId];
    if (!joining_date) {
      setFeedback('Select joining date before sending LOI.');
      return;
    }
    try {
      setFeedback('');
      await apiRequest(`/api/company/hrms/candidates/${candidateId}/send-loi`, {
        method: 'POST',
        body: JSON.stringify({ joining_date })
      });
      setFeedback('LOI sent.');
      await loadData();
      await fetchTimeline(candidateId);
      await refreshSelectionDetail(candidateId);
    } catch (e) {
      setFeedback(e.message);
    }
  };

  const saveToPayrollFromCompensation = async (candidateId) => {
    const s = salaryByCandidate[candidateId] || {};
    try {
      setFeedback('');
      await apiRequest('/api/company/hrms/payroll/salary-structures/from-compensation', {
        method: 'POST',
        body: JSON.stringify({
          candidate_id: candidateId,
          compensation: {
            basic_salary: Number(s.basic_salary) || 0,
            hra: Number(s.hra) || 0,
            conveyance: Number(s.conveyance) || 0,
            other_allowances: Number(s.other_allowances) || 0,
            pf_employee: Number(s.pf_employee) || 0,
            pf_employer: Number(s.pf_employer) || 0
          }
        })
      });
      setFeedback('Compensation saved to payroll.');
    } catch (e) {
      setFeedback(e.message);
    }
  };

  const activateEmployee = async (candidateId) => {
    try {
      setFeedback('');
      await apiRequest(`/api/company/hrms/employees/${candidateId}/activate`, { method: 'POST' });
      setFeedback('Employee activated.');
      await loadData();
    } catch (e) {
      setFeedback(e.message);
    }
  };

  const loadAttendance = useCallback(async () => {
    try {
      const data = await apiRequest(`/api/company/hrms/attendance?month=${attendanceMonth}&year=${attendanceYear}`);
      setAttendanceRows(data.attendance || []);
    } catch (e) {
      setFeedback(e.message);
      setAttendanceRows([]);
    }
  }, [apiRequest, attendanceMonth, attendanceYear]);

  const downloadAttendanceTemplate = async () => {
    const res = await fetch(`${API_URL}/api/company/hrms/attendance/upload-template`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'staffing_attendance_template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const downloadAppAttendance = async (format = 'detail') => {
    try {
      const res = await fetch(
        `${API_URL}/api/company/hrms/attendance/app-export?month=${attendanceMonth}&year=${attendanceYear}&format=${format}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Download failed');
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `staffing_app_attendance_${attendanceYear}_${String(attendanceMonth).padStart(2, '0')}_${format}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      setFeedback('App attendance CSV downloaded.');
    } catch (e) {
      setFeedback(e.message);
    }
  };

  const syncDocumentsByMobile = async () => {
    try {
      const data = await apiRequest('/api/company/hrms/documents/sync-by-mobile', { method: 'POST' });
      setFeedback(data.message || 'Documents synced by mobile.');
      if (documentsCandidateId) await loadEmployeeDocuments(documentsCandidateId);
    } catch (e) {
      setFeedback(e.message);
    }
  };

  const uploadAttendanceSheet = async () => {
    if (!attendanceBulkFile) return;
    try {
      setFeedback('');
      const fd = new FormData();
      fd.append('file', attendanceBulkFile);
      fd.append('month', String(attendanceMonth));
      fd.append('year', String(attendanceYear));
      const res = await fetch(`${API_URL}/api/company/hrms/attendance/upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Upload failed');
      setFeedback(
        `Attendance sheet uploaded (${data.mode} format): ${data.saved} employee(s) saved for ${String(attendanceMonth).padStart(2, '0')}/${attendanceYear}. Payroll will use these present days.`
      );
      setAttendanceBulkFile(null);
      await loadAttendance();
    } catch (e) {
      setFeedback(e.message);
    }
  };

  const clearUploadedAttendance = async (candidateId) => {
    try {
      setFeedback('');
      await apiRequest('/api/company/hrms/attendance/upload', {
        method: 'DELETE',
        body: JSON.stringify({
          candidate_id: candidateId,
          month: attendanceMonth,
          year: attendanceYear
        })
      });
      setFeedback('Uploaded attendance cleared for this employee.');
      await loadAttendance();
    } catch (e) {
      setFeedback(e.message);
    }
  };

  const loadEmployeeDocuments = async (candidateId) => {
    if (!candidateId) {
      setDocumentsData(null);
      return;
    }
    try {
      const data = await apiRequest(`/api/company/hrms/employees/${candidateId}/documents`);
      setDocumentsData(data);
    } catch (e) {
      setFeedback(e.message);
      setDocumentsData(null);
    }
  };

  const copyReviewLink = (path, actionToken) => {
    if (!actionToken) return;
    const url = `${PUBLIC_APP_URL}${path}/${actionToken}`;
    navigator.clipboard.writeText(url).then(() => setFeedback('Manager review link copied.')).catch(() => setFeedback(url));
  };

  useEffect(() => {
    if (activeTab === 'attendance') loadAttendance();
  }, [activeTab, loadAttendance]);

  const loadPayrollPreview = useCallback(async () => {
    setPayrollPreviewLoading(true);
    try {
      const data = await apiRequest(
        `/api/company/hrms/payroll/preview?month=${payrollMonth}&year=${payrollYear}`
      );
      setPayrollPreview(data);
    } catch (e) {
      setPayrollPreview(null);
      setFeedback(e.message);
    } finally {
      setPayrollPreviewLoading(false);
    }
  }, [payrollMonth, payrollYear]);

  useEffect(() => {
    if (activeTab !== 'payroll') return;
    loadPayrollPreview();
  }, [activeTab, loadPayrollPreview]);

  const runPayroll = async (sendEmails = false) => {
    try {
      setPayrollActionLoading(true);
      setFeedback('');
      const data = await apiRequest('/api/company/hrms/payroll/runs', {
        method: 'POST',
        body: JSON.stringify({ month: payrollMonth, year: payrollYear, send_emails: sendEmails })
      });
      const stats = data.stats || {};
      setFeedback(
        `Payroll processed for ${String(payrollMonth).padStart(2, '0')}/${payrollYear}. ` +
          `${stats.payslips || 0} payslip PDF(s) generated. Review amounts, then publish to the mobile app when ready.`
      );
      const runs = await apiRequest('/api/company/hrms/payroll/runs');
      setPayrollRuns(runs.runs || []);
      await loadPayrollPreview();
      const match = (runs.runs || []).find((r) => r.month === payrollMonth && r.year === payrollYear);
      if (match) await loadPayslipsForRun(match.id);
    } catch (e) {
      setFeedback(e.message);
    } finally {
      setPayrollActionLoading(false);
    }
  };

  const publishPayrollToApp = async () => {
    try {
      setPayrollActionLoading(true);
      setFeedback('');
      const data = await apiRequest('/api/company/hrms/payroll/publish-app', {
        method: 'POST',
        body: JSON.stringify({ month: payrollMonth, year: payrollYear })
      });
      setFeedback(data.message || 'Payslips published to the mobile app.');
      const runs = await apiRequest('/api/company/hrms/payroll/runs');
      setPayrollRuns(runs.runs || []);
      await loadPayrollPreview();
    } catch (e) {
      setFeedback(e.message);
    } finally {
      setPayrollActionLoading(false);
    }
  };

  const loadPayslipsForRun = async (runId) => {
    setSelectedRunId(runId);
    try {
      const data = await apiRequest(`/api/company/hrms/payroll/runs/${runId}/payslips`);
      setPayslips(data.payslips || []);
    } catch (e) {
      setFeedback(e.message);
      setPayslips([]);
    }
  };

  const downloadPayslipsZipBlob = async (url, filename) => {
    const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || 'ZIP download failed');
    }
    const blob = await res.blob();
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(objectUrl);
  };

  const downloadPayslipsZip = async (run) => {
    if (!run?.id) return;
    try {
      setFeedback('');
      await downloadPayslipsZipBlob(
        `${API_URL}/api/company/hrms/payroll/runs/${run.id}/payslips.zip`,
        `payslips_${String(run.month).padStart(2, '0')}_${run.year}.zip`
      );
      setFeedback('Payslips ZIP downloaded.');
    } catch (e) {
      setFeedback(e.message);
    }
  };

  const downloadPayslipsZipByMonth = async () => {
    try {
      setPayslipZipLoading(true);
      setFeedback('');
      await downloadPayslipsZipBlob(
        `${API_URL}/api/company/hrms/payslips.zip?month=${payslipZipMonth}&year=${payslipZipYear}`,
        `payslips_${String(payslipZipMonth).padStart(2, '0')}_${payslipZipYear}.zip`
      );
      setFeedback(`Salary slips ZIP downloaded for ${String(payslipZipMonth).padStart(2, '0')}/${payslipZipYear}.`);
    } catch (e) {
      setFeedback(e.message);
    } finally {
      setPayslipZipLoading(false);
    }
  };

  const distributePayslips = async (runId, force = false) => {
    try {
      setFeedback('');
      const data = await apiRequest(`/api/company/hrms/payroll/runs/${runId}/distribute`, {
        method: 'POST',
        body: JSON.stringify({ force })
      });
      const stats = data.stats || {};
      setFeedback(`Emails sent: ${stats.emailsSent || 0}, skipped: ${stats.emailsSkipped || 0}, failed: ${stats.emailsFailed || 0}.`);
      await loadPayslipsForRun(runId);
    } catch (e) {
      setFeedback(e.message);
    }
  };

  const latestAcceptedLetter = (letters, formatIncludes) => {
    const matches = (letters || []).filter(
      (l) =>
        String(l.offer_letter_format || '')
          .toLowerCase()
          .includes(formatIncludes) && String(l.status || '').toLowerCase() === 'accepted'
    );
    if (!matches.length) return null;
    return matches.sort((a, b) => {
      const aTs = new Date(a.updated_at || a.created_at || 0).getTime();
      const bTs = new Date(b.updated_at || b.created_at || 0).getTime();
      return bTs - aTs;
    })[0];
  };

  const downloadHrmsOfferLetter = async (letterId, label) => {
    if (!letterId || !token) return;
    try {
      setFeedback('');
      const res = await fetch(`${API_URL}/api/company/hrms/offer-letters/${letterId}/download`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || `Failed to download ${label}`);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${label.replace(/\s+/g, '_')}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      setFeedback(`${label} downloaded.`);
    } catch (e) {
      setFeedback(e.message);
    }
  };

  const timelineSummary = (timelineData) => {
    const letters = timelineData?.letters || [];
    if (!letters.length) return 'No letters yet';
    const latestByType = {};
    letters.forEach((l) => {
      const key = String(l.offer_letter_format || 'letter').toLowerCase();
      const curr = latestByType[key];
      if (!curr) {
        latestByType[key] = l;
        return;
      }
      const currTs = new Date(curr.updated_at || curr.created_at || 0).getTime();
      const nextTs = new Date(l.updated_at || l.created_at || 0).getTime();
      if (nextTs >= currTs) latestByType[key] = l;
    });
    return Object.values(latestByType)
      .sort((a, b) => {
        const aTs = new Date(a.updated_at || a.created_at || 0).getTime();
        const bTs = new Date(b.updated_at || b.created_at || 0).getTime();
        return aTs - bTs;
      })
      .map((l) => `${l.offer_letter_format}:${l.status}`)
      .join(' | ');
  };

  return (
    <Box sx={{ height: '100vh', bgcolor: DASH.bg, overflow: 'hidden' }}>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '280px 1fr' }, height: '100vh' }}>
        <Box
          sx={{
            background: 'linear-gradient(180deg, #1e293b 0%, #0f172a 100%)',
            color: '#fff',
            p: 2.5,
            borderRight: '1px solid rgba(255,255,255,0.06)',
            height: '100vh',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2, flexShrink: 0 }}>
            <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)' }}>
              <BusinessIcon />
            </Avatar>
            <Box>
              <Typography variant="subtitle1" fontWeight={800}>
                Staffing HRMS
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.85 }}>
                {user.company_name || 'Company'}
              </Typography>
            </Box>
          </Stack>
          <Box sx={{ flex: 1, overflowY: 'auto', pr: 0.5 }}>
            <List sx={{ p: 0 }}>
              {navItems.map((item) => (
                <ListItemButton
                  key={item.id}
                  selected={activeTab === item.id}
                  onClick={() => setActiveTab(item.id)}
                  sx={{
                    borderRadius: 2,
                    mb: 0.5,
                    color: '#e2e8f0',
                    '&.Mui-selected': { bgcolor: 'rgba(255,255,255,0.1)', color: '#fff', borderLeft: '3px solid #14b8a6' }
                  }}
                >
                  <ListItemIcon sx={{ color: 'inherit', minWidth: 32 }}>{item.icon}</ListItemIcon>
                  <ListItemText primary={item.label} primaryTypographyProps={{ fontSize: 14, fontWeight: 600 }} />
                </ListItemButton>
              ))}
            </List>
          </Box>
          <Box sx={{ flexShrink: 0, pt: 1 }}>
            <Divider sx={{ my: 2, borderColor: 'rgba(255,255,255,0.2)' }} />
            <Button fullWidth variant="outlined" color="inherit" startIcon={<LogoutIcon />} onClick={onLogout}>
              Logout
            </Button>
          </Box>
        </Box>

        <Box sx={{ height: '100vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          <Box
            sx={{
              px: { xs: 2, md: 3 },
              py: 2,
              background: '#fff',
              borderBottom: '1px solid #e2e8f0',
              position: 'sticky',
              top: 0,
              zIndex: 10
            }}
          >
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} justifyContent="space-between" alignItems={{ md: 'center' }}>
              <Box>
                <Typography variant="h5" fontWeight={800} color="#0f172a">
                  {navItems.find((n) => n.id === activeTab)?.label || 'Dashboard'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {activeTab === 'overview'
                    ? 'Workforce analytics and operational priorities'
                    : 'Employer-side HRMS control panel for staffing operations'}
                </Typography>
              </Box>
              <Stack direction="row" spacing={1} alignItems="center">
                <Chip label="Staffing Company" color="success" variant="outlined" size="small" />
                <Button size="small" variant="contained" onClick={loadData} sx={{ bgcolor: '#0d9488' }}>
                  Refresh
                </Button>
              </Stack>
            </Stack>
          </Box>

          <Box sx={{ flex: 1, overflowY: 'auto' }}>
            <Container maxWidth="xl" sx={{ py: 3 }}>
              {error && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}
              {!!feedback && (
                <Alert severity="info" sx={{ mb: 2 }} onClose={() => setFeedback('')}>
                  {feedback}
                </Alert>
              )}

              {activeTab === 'overview' && (
                <Stack spacing={3}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: { xs: 2, md: 2.5 },
                      border: `1px solid ${DASH.border}`,
                      borderRadius: 1.5,
                      bgcolor: DASH.card,
                      backgroundImage: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)'
                    }}
                  >
                    <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ md: 'center' }} spacing={2}>
                      <Box>
                        <Typography variant="overline" sx={{ color: DASH.accent, fontWeight: 700, letterSpacing: '0.12em' }}>
                          Executive overview
                        </Typography>
                        <Typography variant="h5" sx={{ color: DASH.text, fontWeight: 700, mt: 0.25 }}>
                          {user.company_name || 'Staffing operations'}
                        </Typography>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.75 }}>
                          <CalendarTodayIcon sx={{ fontSize: 16, color: DASH.muted }} />
                          <Typography variant="body2" color={DASH.muted}>
                            {dashboardDateLabel}
                          </Typography>
                        </Stack>
                      </Box>
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                        <Chip
                          size="small"
                          label={`${activeRate}% active`}
                          sx={{ bgcolor: DASH.accentSoft, color: DASH.accent, fontWeight: 600, border: `1px solid ${DASH.accent}33` }}
                        />
                        {overview.holidays > 0 && (
                          <Chip size="small" variant="outlined" label={`${overview.holidays} holidays this year`} />
                        )}
                      </Stack>
                    </Stack>
                  </Paper>

                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)', xl: 'repeat(5, 1fr)' },
                      gap: 2
                    }}
                  >
                    <DashboardStatCard label="Total headcount" value={overview.totalEmployees} sublabel="All staffing employments" icon={<GroupsIcon />} />
                    <DashboardStatCard
                      label="Active"
                      value={overview.activeEmployees}
                      sublabel={`${activeRate}% of workforce`}
                      icon={<ActiveIcon />}
                      accent="#047857"
                      onClick={() => setActiveTab('employees')}
                    />
                    <DashboardStatCard label="On leave today" value={overview.employeesOnLeaveToday} icon={<LeaveIcon />} accent="#b45309" onClick={() => setActiveTab('leaveRequests')} />
                    <DashboardStatCard
                      label="Pending leave"
                      value={overview.pendingLeaves}
                      sublabel="Requires manager action"
                      icon={<PendingIcon />}
                      accent="#6d28d9"
                      onClick={() => setActiveTab('leaveRequests')}
                    />
                    <DashboardStatCard
                      label="Pending LOI / join"
                      value={overview.pendingLoiJoin}
                      icon={<PersonAddIcon />}
                      accent="#0369a1"
                      onClick={() => setActiveTab('selectionCandidates')}
                    />
                  </Box>

                  <Grid container spacing={2}>
                    <Grid item xs={12} lg={8}>
                      <Card elevation={0} sx={{ border: `1px solid ${DASH.border}`, borderRadius: 1.5 }}>
                        <CardHeader
                          title="Workforce distribution"
                          subheader="Operational breakdown across your staffing roster"
                          titleTypographyProps={{ variant: 'subtitle1', fontWeight: 700, color: DASH.text }}
                          subheaderTypographyProps={{ variant: 'caption', color: DASH.muted }}
                          sx={{ pb: 0 }}
                        />
                        <CardContent>
                          <Stack spacing={2.25}>
                            {workforceRows.map((row) => (
                              <Box key={row.label}>
                                <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.75 }}>
                                  <Typography variant="body2" sx={{ color: DASH.text, fontWeight: 500 }}>
                                    {row.label}
                                  </Typography>
                                  <Typography variant="body2" sx={{ color: DASH.muted, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                                    {row.value}
                                  </Typography>
                                </Stack>
                                <LinearProgress
                                  variant="determinate"
                                  value={pct(row.value, maxWorkforce)}
                                  sx={{
                                    height: 6,
                                    borderRadius: 0.5,
                                    bgcolor: '#f1f5f9',
                                    '& .MuiLinearProgress-bar': { borderRadius: 0.5, bgcolor: row.color }
                                  }}
                                />
                              </Box>
                            ))}
                          </Stack>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={12} lg={4}>
                      <Card elevation={0} sx={{ border: `1px solid ${DASH.border}`, borderRadius: 1.5, height: '100%' }}>
                        <CardHeader
                          title="Composition"
                          subheader="Active · on leave · awaiting activation"
                          titleTypographyProps={{ variant: 'subtitle1', fontWeight: 700, color: DASH.text }}
                          subheaderTypographyProps={{ variant: 'caption', color: DASH.muted }}
                          sx={{ pb: 0 }}
                        />
                        <CardContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                          <TeamHealthRing active={overview.activeEmployees} onLeave={overview.employeesOnLeaveToday} inactive={inactiveEmployees} />
                          <Stack spacing={1} sx={{ mt: 2.5, width: '100%' }}>
                            {[
                              { label: 'Active', value: overview.activeEmployees, color: '#047857' },
                              { label: 'On leave', value: overview.employeesOnLeaveToday, color: '#b45309' },
                              { label: 'Awaiting activation', value: inactiveEmployees, color: '#64748b' }
                            ].map((item) => (
                              <Stack key={item.label} direction="row" justifyContent="space-between" alignItems="center">
                                <Stack direction="row" spacing={1} alignItems="center">
                                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: item.color }} />
                                  <Typography variant="body2" color={DASH.muted}>
                                    {item.label}
                                  </Typography>
                                </Stack>
                                <Typography variant="body2" fontWeight={600} color={DASH.text}>
                                  {item.value}
                                </Typography>
                              </Stack>
                            ))}
                          </Stack>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>

                  <Grid container spacing={2}>
                    <Grid item xs={12} md={5}>
                      <Card elevation={0} sx={{ border: `1px solid ${DASH.border}`, borderRadius: 1.5, height: '100%' }}>
                        <CardHeader
                          title="Requires attention"
                          titleTypographyProps={{ variant: 'subtitle1', fontWeight: 700, color: DASH.text }}
                          sx={{ pb: 0 }}
                        />
                        <CardContent>
                          {attentionItems.length === 0 ? (
                            <Typography variant="body2" color={DASH.muted}>
                              No outstanding HR actions. All queues are clear.
                            </Typography>
                          ) : (
                            <Stack spacing={1}>
                              {attentionItems.map((item) => (
                                <Paper
                                  key={item.label}
                                  variant="outlined"
                                  sx={{
                                    p: 1.5,
                                    borderRadius: 1,
                                    borderColor: DASH.border,
                                    cursor: 'pointer',
                                    '&:hover': { bgcolor: '#f8fafc', borderColor: DASH.accent }
                                  }}
                                  onClick={() => setActiveTab(item.tab)}
                                >
                                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                                    <Box>
                                      <Typography variant="body2" fontWeight={600} color={DASH.text}>
                                        {item.label}
                                      </Typography>
                                      <Chip size="small" color={item.severity} label={item.count} sx={{ mt: 0.5, height: 22 }} />
                                    </Box>
                                    <ArrowForwardIcon sx={{ color: DASH.muted, fontSize: 18 }} />
                                  </Stack>
                                </Paper>
                              ))}
                            </Stack>
                          )}
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={12} md={7}>
                      <Card elevation={0} sx={{ border: `1px solid ${DASH.border}`, borderRadius: 1.5, height: '100%' }}>
                        <CardHeader
                          title="Quick navigation"
                          subheader="Core HRMS modules"
                          titleTypographyProps={{ variant: 'subtitle1', fontWeight: 700, color: DASH.text }}
                          subheaderTypographyProps={{ variant: 'caption', color: DASH.muted }}
                          sx={{ pb: 0 }}
                        />
                        <CardContent>
                          <Grid container spacing={1}>
                            {[
                              { tab: 'employees', label: 'Employees', icon: <GroupIcon fontSize="small" /> },
                              { tab: 'attendance', label: 'Attendance', icon: <AttendanceIcon fontSize="small" /> },
                              { tab: 'payroll', label: 'Payroll', icon: <PayrollIcon fontSize="small" /> },
                              { tab: 'leaveRequests', label: 'Leave requests', icon: <LeaveRequestsIcon fontSize="small" /> },
                              { tab: 'grievances', label: 'Grievance center', icon: <GrievanceIcon fontSize="small" /> },
                              { tab: 'documents', label: 'Documents', icon: <DocumentsIcon fontSize="small" /> }
                            ].map((action) => (
                              <Grid item xs={6} sm={4} key={action.tab}>
                                <Button
                                  fullWidth
                                  variant="outlined"
                                  startIcon={action.icon}
                                  onClick={() => setActiveTab(action.tab)}
                                  sx={{
                                    justifyContent: 'flex-start',
                                    py: 1.25,
                                    borderColor: DASH.border,
                                    color: DASH.text,
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    '&:hover': { borderColor: DASH.accent, bgcolor: DASH.accentSoft }
                                  }}
                                >
                                  {action.label}
                                </Button>
                              </Grid>
                            ))}
                          </Grid>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>

                  <Card elevation={0} sx={{ border: `1px solid ${DASH.border}`, borderRadius: 1.5 }}>
                    <CardHeader
                      title="Pending leave requests"
                      subheader="Most recent submissions awaiting approval"
                      action={
                        <Button size="small" endIcon={<ArrowForwardIcon />} onClick={() => setActiveTab('leaveRequests')} sx={{ color: DASH.accent }}>
                          View all
                        </Button>
                      }
                      titleTypographyProps={{ variant: 'subtitle1', fontWeight: 700, color: DASH.text }}
                      subheaderTypographyProps={{ variant: 'caption', color: DASH.muted }}
                      sx={{ pb: 0 }}
                    />
                    <CardContent sx={{ pt: 0 }}>
                      {pendingLeavePreview.length === 0 ? (
                        <Typography variant="body2" color={DASH.muted} sx={{ py: 2 }}>
                          No pending leave requests at this time.
                        </Typography>
                      ) : (
                        <TableContainer>
                          <Table size="small">
                            <TableHead>
                              <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', color: DASH.muted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Employee</TableCell>
                                <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', color: DASH.muted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Type</TableCell>
                                <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', color: DASH.muted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Period</TableCell>
                                <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', color: DASH.muted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Days</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {pendingLeavePreview.map((r) => (
                                <TableRow key={r.id} hover sx={{ '&:last-child td': { border: 0 } }}>
                                  <TableCell>
                                    <Typography variant="body2" fontWeight={600}>
                                      {r.employee_name}
                                    </Typography>
                                  </TableCell>
                                  <TableCell sx={{ textTransform: 'capitalize' }}>{r.leave_type}</TableCell>
                                  <TableCell>
                                    <Typography variant="body2" color={DASH.muted}>
                                      {r.from_date} — {r.to_date}
                                    </Typography>
                                  </TableCell>
                                  <TableCell>{r.days}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      )}
                    </CardContent>
                  </Card>
                </Stack>
              )}

              {activeTab === 'employees' && (
                <Stack spacing={2}>
                  {pendingActivationList.length > 0 && (
                    <>
                      <Typography variant="h6" fontWeight={700}>
                        Pending activation (LOI accepted)
                      </Typography>
                      <Stack spacing={1} sx={{ mb: 2 }}>
                        {pendingActivationList.map((e) => (
                          <Card key={`pending-${e.id}`} variant="outlined" sx={{ borderRadius: 2, borderColor: '#fbbf24' }}>
                            <CardContent sx={{ py: 1.5 }}>
                              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} justifyContent="space-between">
                                <Box>
                                  <Stack direction="row" spacing={1} alignItems="center">
                                    <Typography fontWeight={700}>{e.full_name}</Typography>
                                    <Chip size="small" label="LOI accepted" color="warning" />
                                  </Stack>
                                  <Typography variant="caption" color="text.secondary">
                                    Joining: {e.joining_date || '—'} · {[e.email, e.mobile].filter(Boolean).join(' · ')}
                                  </Typography>
                                </Box>
                                <Button variant="contained" color="success" onClick={() => activateEmployee(e.id)}>
                                  Activate
                                </Button>
                              </Stack>
                            </CardContent>
                          </Card>
                        ))}
                      </Stack>
                    </>
                  )}
                  <Typography variant="h6" fontWeight={700}>
                    Active employees
                  </Typography>
                  {activeManagers.length === 0 && activeEmployeesList.length > 0 && (
                    <Alert severity="info">
                      Add managers under the <strong>Managers</strong> tab before assigning reporting managers.
                    </Alert>
                  )}
                  {activeEmployeesList.length === 0 ? (
                    <Typography color="text.secondary">No active employees yet.</Typography>
                  ) : (
                    <Stack spacing={1}>
                      {activeEmployeesList.map((e) => (
                        <Card key={e.id} variant="outlined" sx={{ borderRadius: 2 }}>
                          <CardContent sx={{ py: 1.5 }}>
                            <Stack
                              direction={{ xs: 'column', sm: 'row' }}
                              spacing={2}
                              alignItems={{ sm: 'center' }}
                              justifyContent="space-between"
                            >
                              <Box>
                                <Stack direction="row" spacing={1} alignItems="center">
                                  <Typography fontWeight={700}>{e.full_name}</Typography>
                                  <Chip size="small" label="Active" color="success" />
                                </Stack>
                                <Typography variant="caption" color="text.secondary" display="block">
                                  {[e.email, e.mobile, e.employee_code].filter(Boolean).join(' · ')}
                                </Typography>
                              </Box>
                              <TextField
                                select
                                size="small"
                                label="Reporting manager"
                                value={e.reporting_manager_id ?? ''}
                                onChange={(ev) => {
                                  const val = ev.target.value;
                                  assignEmployeeManager(e.id, val === '' ? null : Number(val));
                                }}
                                disabled={assigningManagerFor === e.id || activeManagers.length === 0}
                                sx={{ minWidth: { sm: 220 } }}
                              >
                                <MenuItem value="">
                                  <em>Unassigned</em>
                                </MenuItem>
                                {activeManagers.map((m) => (
                                  <MenuItem key={m.id} value={m.id}>
                                    {m.name}
                                    {m.designation ? ` · ${m.designation}` : ''}
                                  </MenuItem>
                                ))}
                              </TextField>
                            </Stack>
                          </CardContent>
                        </Card>
                      ))}
                    </Stack>
                  )}
                </Stack>
              )}

              {activeTab === 'managers' && (
                <Stack spacing={2}>
                  <Typography variant="h6" fontWeight={700}>
                    Managers
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Create managers for your staffing company, then assign them to active employees on the Employees tab.
                  </Typography>
                  <Card variant="outlined" sx={{ borderRadius: 2 }}>
                    <CardContent>
                      <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                        {editingManagerId ? 'Edit manager' : 'Add manager'}
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6} md={3}>
                          <TextField
                            label="Name"
                            required
                            fullWidth
                            size="small"
                            value={managerForm.name}
                            onChange={(ev) => setManagerForm((f) => ({ ...f, name: ev.target.value }))}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                          <TextField
                            label="Email"
                            fullWidth
                            size="small"
                            value={managerForm.email}
                            onChange={(ev) => setManagerForm((f) => ({ ...f, email: ev.target.value }))}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                          <TextField
                            label="Mobile"
                            fullWidth
                            size="small"
                            value={managerForm.mobile}
                            onChange={(ev) => setManagerForm((f) => ({ ...f, mobile: ev.target.value }))}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                          <TextField
                            label="Designation"
                            fullWidth
                            size="small"
                            value={managerForm.designation}
                            onChange={(ev) => setManagerForm((f) => ({ ...f, designation: ev.target.value }))}
                          />
                        </Grid>
                      </Grid>
                      <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                        <Button variant="contained" onClick={saveManager}>
                          {editingManagerId ? 'Save changes' : 'Add manager'}
                        </Button>
                        {editingManagerId && (
                          <Button variant="outlined" onClick={resetManagerForm}>
                            Cancel
                          </Button>
                        )}
                      </Stack>
                    </CardContent>
                  </Card>
                  {managers.length === 0 ? (
                    <Typography color="text.secondary">No managers yet.</Typography>
                  ) : (
                    <Stack spacing={1}>
                      {managers.map((m) => (
                        <Card key={m.id} variant="outlined" sx={{ borderRadius: 2 }}>
                          <CardContent sx={{ py: 1.5 }}>
                            <Stack
                              direction={{ xs: 'column', sm: 'row' }}
                              spacing={1}
                              alignItems={{ sm: 'center' }}
                              justifyContent="space-between"
                            >
                              <Box>
                                <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
                                  <Typography fontWeight={700}>{m.name}</Typography>
                                  <Chip
                                    size="small"
                                    label={m.status === 'active' ? 'Active' : 'Inactive'}
                                    color={m.status === 'active' ? 'success' : 'default'}
                                    variant={m.status === 'active' ? 'filled' : 'outlined'}
                                  />
                                  {(m.active_report_count ?? 0) > 0 && (
                                    <Chip size="small" variant="outlined" label={`${m.active_report_count} reports`} />
                                  )}
                                </Stack>
                                <Typography variant="caption" color="text.secondary">
                                  {[m.designation, m.email, m.mobile].filter(Boolean).join(' · ') || 'No contact details'}
                                </Typography>
                              </Box>
                              <Stack direction="row" spacing={1}>
                                <Button size="small" onClick={() => startEditManager(m)}>
                                  Edit
                                </Button>
                                <Button size="small" color={m.status === 'active' ? 'warning' : 'success'} onClick={() => toggleManagerStatus(m)}>
                                  {m.status === 'active' ? 'Deactivate' : 'Reactivate'}
                                </Button>
                              </Stack>
                            </Stack>
                          </CardContent>
                        </Card>
                      ))}
                    </Stack>
                  )}
                </Stack>
              )}

              {activeTab === 'selectionCandidates' &&
                (selectionDetailId ? (
                  (() => {
                    const c = selectionCandidates.find((x) => x.id === selectionDetailId);
                    if (!c) {
                      return (
                        <Stack spacing={2}>
                          <Button
                            variant="outlined"
                            startIcon={<ArrowBackIcon />}
                            onClick={closeSelectionDetail}
                            sx={{ alignSelf: 'flex-start' }}
                          >
                            Back to selection list
                          </Button>
                          <Alert severity="warning">This candidate is no longer in your selection list.</Alert>
                        </Stack>
                      );
                    }
                    const cid = c.id;
                    let submitted = detailProfile?.profile_data;
                    if (submitted && typeof submitted === 'string') {
                      try {
                        submitted = JSON.parse(submitted);
                      } catch {
                        submitted = null;
                      }
                    }
                    const files = submitted?.files || {};
                    const fileHref = (p) => (p && API_URL ? `${API_URL}${p}` : null);
                    return (
                      <Stack spacing={2}>
                        <Button
                          variant="outlined"
                          startIcon={<ArrowBackIcon />}
                          onClick={closeSelectionDetail}
                          sx={{ alignSelf: 'flex-start' }}
                        >
                          Back to selection list
                        </Button>

                        <Card sx={{ borderRadius: 2, boxShadow: '0 4px 20px rgba(15,23,42,0.06)' }}>
                          <CardHeader
                            title={
                              <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
                                <Typography variant="h6" fontWeight={800} component="span">
                                  {c.full_name}
                                </Typography>
                                <Chip size="small" label={c.status || 'selection'} />
                                {c.profile_completed_at ? (
                                  <Chip size="small" color="success" label="Profile complete" />
                                ) : c.profile_invite_sent ? (
                                  <Chip size="small" color="warning" variant="outlined" label="Awaiting candidate" />
                                ) : (
                                  <Chip size="small" variant="outlined" label="Profile not started" />
                                )}
                                {c.joining_date && (
                                  <Chip size="small" color="info" variant="outlined" label={`Joining: ${c.joining_date}`} />
                                )}
                              </Stack>
                            }
                            subheader={[c.email, c.mobile].filter(Boolean).join(' · ') || 'No email on file — profile link can still be copied and shared manually.'}
                            action={
                              <Button size="small" variant="text" onClick={() => refreshSelectionDetail()} disabled={detailProfileLoading}>
                                Refresh
                              </Button>
                            }
                          />
                        </Card>

                        <Card variant="outlined" sx={{ borderRadius: 2 }}>
                          <CardHeader title="1 · Profile link" subheader="Send the link (same idea as supervisor registration). Candidate completes details and uploads documents." />
                          <CardContent>
                            <Button
                              variant="contained"
                              color="secondary"
                              startIcon={<LinkIcon />}
                              onClick={() => sendProfileInvite(cid)}
                            >
                              Send profile link
                            </Button>
                          </CardContent>
                        </Card>

                        <Card variant="outlined" sx={{ borderRadius: 2 }}>
                          <CardHeader
                            title="2 · What the candidate submitted"
                            subheader="Updates when they complete the profile form."
                          />
                          <CardContent>
                            {detailProfileLoading && <LinearProgress sx={{ mb: 2 }} />}
                            {detailProfileError && (
                              <Alert severity="error" sx={{ mb: 2 }}>
                                {detailProfileError}
                              </Alert>
                            )}
                            {!detailProfileLoading && detailProfile && (
                              <Stack spacing={1.5}>
                                {detailProfile.qualification && (
                                  <Typography variant="body2">
                                    <strong>Qualification:</strong> {detailProfile.qualification}{' '}
                                    {detailProfile.course ? `· ${detailProfile.course}` : ''}{' '}
                                    {detailProfile.specialization ? `· ${detailProfile.specialization}` : ''}
                                  </Typography>
                                )}
                                {detailProfile.profile_completed_at ? (
                                  <Chip
                                    size="small"
                                    color="success"
                                    label={`Submitted ${new Date(detailProfile.profile_completed_at).toLocaleString()}`}
                                  />
                                ) : (
                                  <Chip size="small" label="Not submitted yet" />
                                )}
                                {submitted && (
                                  <Box sx={{ mt: 1 }}>
                                    <Typography fontWeight={700} gutterBottom>
                                      Submitted details
                                    </Typography>
                                    {['address', 'experience_years', 'emergency_name', 'emergency_contact'].map((key) =>
                                      submitted[key] ? (
                                        <Typography key={key} variant="body2">
                                          <strong>{key.replace(/_/g, ' ')}:</strong> {String(submitted[key])}
                                        </Typography>
                                      ) : null
                                    )}
                                    <Typography fontWeight={700} sx={{ mt: 2 }} gutterBottom>
                                      Documents
                                    </Typography>
                                    <Stack spacing={0.5}>
                                      {files.resume && (
                                        <Button size="small" href={fileHref(files.resume)} target="_blank" rel="noreferrer">
                                          Resume
                                        </Button>
                                      )}
                                      {files.education_proof && (
                                        <Button size="small" href={fileHref(files.education_proof)} target="_blank" rel="noreferrer">
                                          Education proof
                                        </Button>
                                      )}
                                      {(files.other_documents || []).map((p, i) => (
                                        <Button key={i} size="small" href={fileHref(p)} target="_blank" rel="noreferrer">
                                          Other document {i + 1}
                                        </Button>
                                      ))}
                                      {!files.resume && !files.education_proof && !(files.other_documents || []).length && (
                                        <Typography variant="caption" color="text.secondary">
                                          No files on record yet.
                                        </Typography>
                                      )}
                                    </Stack>
                                  </Box>
                                )}
                              </Stack>
                            )}
                          </CardContent>
                        </Card>

                        <Card variant="outlined" sx={{ borderRadius: 2 }}>
                          <CardHeader
                            title="3 · Designation & compensation"
                            subheader="Fill these before sending the provisional offer. All fields stay visible here — no collapsible sections."
                          />
                          <CardContent>
                            <Grid container spacing={2}>
                              <Grid item xs={12} sm={6} md={4}>
                                <TextField
                                  size="small"
                                  fullWidth
                                  label="Designation"
                                  value={salaryByCandidate[cid]?.designation || ''}
                                  onChange={(e) =>
                                    setSalaryByCandidate((p) => ({
                                      ...p,
                                      [cid]: { ...(p[cid] || {}), designation: e.target.value }
                                    }))
                                  }
                                />
                              </Grid>
                              <Grid item xs={12} sm={6} md={4}>
                                <TextField
                                  size="small"
                                  fullWidth
                                  label="Location"
                                  value={salaryByCandidate[cid]?.location || ''}
                                  onChange={(e) =>
                                    setSalaryByCandidate((p) => ({
                                      ...p,
                                      [cid]: { ...(p[cid] || {}), location: e.target.value }
                                    }))
                                  }
                                />
                              </Grid>
                              <Grid item xs={12} sm={6} md={4}>
                                <TextField
                                  size="small"
                                  fullWidth
                                  label="Basic"
                                  type="number"
                                  value={salaryByCandidate[cid]?.basic_salary || ''}
                                  onChange={(e) =>
                                    setSalaryByCandidate((p) => ({
                                      ...p,
                                      [cid]: { ...(p[cid] || {}), basic_salary: e.target.value }
                                    }))
                                  }
                                />
                              </Grid>
                              <Grid item xs={12} sm={6} md={4}>
                                <TextField
                                  size="small"
                                  fullWidth
                                  label="HRA"
                                  type="number"
                                  value={salaryByCandidate[cid]?.hra || ''}
                                  onChange={(e) =>
                                    setSalaryByCandidate((p) => ({ ...p, [cid]: { ...(p[cid] || {}), hra: e.target.value } }))
                                  }
                                />
                              </Grid>
                              <Grid item xs={12} sm={6} md={4}>
                                <TextField
                                  size="small"
                                  fullWidth
                                  label="Conveyance"
                                  type="number"
                                  value={salaryByCandidate[cid]?.conveyance || ''}
                                  onChange={(e) =>
                                    setSalaryByCandidate((p) => ({
                                      ...p,
                                      [cid]: { ...(p[cid] || {}), conveyance: e.target.value }
                                    }))
                                  }
                                />
                              </Grid>
                              <Grid item xs={12} sm={6} md={4}>
                                <TextField
                                  size="small"
                                  fullWidth
                                  label="Other allowances"
                                  type="number"
                                  value={salaryByCandidate[cid]?.other_allowances || ''}
                                  onChange={(e) =>
                                    setSalaryByCandidate((p) => ({
                                      ...p,
                                      [cid]: { ...(p[cid] || {}), other_allowances: e.target.value }
                                    }))
                                  }
                                />
                              </Grid>
                              <Grid item xs={12} sm={6} md={4}>
                                <TextField
                                  size="small"
                                  fullWidth
                                  label="Gross pay"
                                  type="number"
                                  value={salaryByCandidate[cid]?.gross_pay || ''}
                                  onChange={(e) =>
                                    setSalaryByCandidate((p) => ({
                                      ...p,
                                      [cid]: { ...(p[cid] || {}), gross_pay: e.target.value }
                                    }))
                                  }
                                />
                              </Grid>
                              <Grid item xs={12} sm={6} md={4}>
                                <TextField
                                  size="small"
                                  fullWidth
                                  label="PF (employee)"
                                  type="number"
                                  value={salaryByCandidate[cid]?.pf_employee || ''}
                                  onChange={(e) =>
                                    setSalaryByCandidate((p) => ({
                                      ...p,
                                      [cid]: { ...(p[cid] || {}), pf_employee: e.target.value }
                                    }))
                                  }
                                />
                              </Grid>
                              <Grid item xs={12} sm={6} md={4}>
                                <TextField
                                  size="small"
                                  fullWidth
                                  label="PF (employer)"
                                  type="number"
                                  value={salaryByCandidate[cid]?.pf_employer || ''}
                                  onChange={(e) =>
                                    setSalaryByCandidate((p) => ({
                                      ...p,
                                      [cid]: { ...(p[cid] || {}), pf_employer: e.target.value }
                                    }))
                                  }
                                />
                              </Grid>
                              <Grid item xs={12} sm={6} md={4}>
                                <TextField
                                  size="small"
                                  fullWidth
                                  label="Net pay"
                                  type="number"
                                  value={salaryByCandidate[cid]?.net_pay || ''}
                                  onChange={(e) =>
                                    setSalaryByCandidate((p) => ({
                                      ...p,
                                      [cid]: { ...(p[cid] || {}), net_pay: e.target.value }
                                    }))
                                  }
                                />
                              </Grid>
                              <Grid item xs={12} sm={6} md={4}>
                                <TextField
                                  size="small"
                                  fullWidth
                                  label="CTC"
                                  type="number"
                                  value={salaryByCandidate[cid]?.ctc || ''}
                                  onChange={(e) =>
                                    setSalaryByCandidate((p) => ({ ...p, [cid]: { ...(p[cid] || {}), ctc: e.target.value } }))
                                  }
                                />
                              </Grid>
                            </Grid>
                            <Button sx={{ mt: 2 }} variant="outlined" onClick={() => saveToPayrollFromCompensation(cid)}>
                              Save to payroll
                            </Button>
                          </CardContent>
                        </Card>

                        <Card variant="outlined" sx={{ borderRadius: 2 }}>
                          <CardHeader title="4 · Offers & LOI" subheader="Provisional first, then LOI with joining date after they accept." />
                          <CardContent>
                            {(() => {
                              const tl = timelineByCandidate[cid];
                              const letters = tl?.letters || [];
                              const acceptedProv = latestAcceptedLetter(letters, 'provisional');
                              const acceptedLoi = latestAcceptedLetter(letters, 'loi');
                              const isLoiAccepted = ['loi_accepted', 'active'].includes(c.status);
                              const isLoiSent = c.status === 'loi_sent';
                              const joiningDisplay =
                                c.joining_date ||
                                tl?.employment?.joining_date ||
                                acceptedLoi?.joining_date ||
                                joiningDateByCandidate[cid] ||
                                '';
                              const provAccepted =
                                !!acceptedProv ||
                                ['provisional_accepted', 'loi_sent', 'loi_accepted', 'active'].includes(c.status);

                              if (isLoiAccepted) {
                                return (
                                  <Stack spacing={2}>
                                    <Alert severity="success">
                                      LOI accepted. Joining and offer documents are locked; download copies below.
                                    </Alert>
                                    <Box>
                                      <Typography variant="caption" color="text.secondary" display="block">
                                        Joining date
                                      </Typography>
                                      <Typography variant="h6" fontWeight={700}>
                                        {joiningDisplay
                                          ? new Date(joiningDisplay).toLocaleDateString(undefined, {
                                              day: 'numeric',
                                              month: 'long',
                                              year: 'numeric'
                                            })
                                          : '—'}
                                      </Typography>
                                    </Box>
                                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} flexWrap="wrap" useFlexGap>
                                      {acceptedProv?.id ? (
                                        <Button
                                          variant="contained"
                                          startIcon={<DownloadIcon />}
                                          onClick={() => downloadHrmsOfferLetter(acceptedProv.id, 'Provisional_offer_accepted')}
                                        >
                                          Download offer letter (accepted)
                                        </Button>
                                      ) : (
                                        <Typography variant="body2" color="text.secondary">
                                          Provisional offer PDF not found.
                                        </Typography>
                                      )}
                                      {acceptedLoi?.id ? (
                                        <Button
                                          variant="contained"
                                          color="secondary"
                                          startIcon={<DownloadIcon />}
                                          onClick={() => downloadHrmsOfferLetter(acceptedLoi.id, 'LOI_accepted')}
                                        >
                                          Download LOI (accepted)
                                        </Button>
                                      ) : (
                                        <Typography variant="body2" color="text.secondary">
                                          LOI PDF not found.
                                        </Typography>
                                      )}
                                    </Stack>
                                  </Stack>
                                );
                              }

                              return (
                                <Stack spacing={2}>
                                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} flexWrap="wrap" useFlexGap>
                                    <Tooltip
                                      title={
                                        ['selection', 'inactive'].includes(c.status) && !c.profile_completed_at
                                          ? 'Candidate must complete their profile via the invite link first.'
                                          : provAccepted
                                            ? 'Provisional offer already accepted.'
                                            : ''
                                      }
                                    >
                                      <span>
                                        <Button
                                          variant="contained"
                                          onClick={() => sendProvisional(cid)}
                                          disabled={
                                            provAccepted ||
                                            !['selection', 'inactive'].includes(c.status) ||
                                            !c.profile_completed_at
                                          }
                                        >
                                          Send provisional offer
                                        </Button>
                                      </span>
                                    </Tooltip>
                                    {c.status === 'provisional_sent' && (
                                      <Button variant="outlined" color="warning" onClick={() => resendProvisional(cid)}>
                                        Resend provisional
                                      </Button>
                                    )}
                                    {acceptedProv?.id && (
                                      <Button
                                        variant="outlined"
                                        startIcon={<DownloadIcon />}
                                        onClick={() => downloadHrmsOfferLetter(acceptedProv.id, 'Provisional_offer_accepted')}
                                      >
                                        Download offer (accepted)
                                      </Button>
                                    )}
                                  </Stack>
                                  <Divider />
                                  {isLoiSent && (
                                    <Alert severity="info">LOI sent — awaiting candidate acceptance.</Alert>
                                  )}
                                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
                                    {isLoiSent || joiningDisplay ? (
                                      <Box sx={{ minWidth: 200 }}>
                                        <Typography variant="caption" color="text.secondary" display="block">
                                          Joining date
                                        </Typography>
                                        <Typography fontWeight={700}>
                                          {joiningDisplay
                                            ? new Date(joiningDisplay).toLocaleDateString()
                                            : '—'}
                                        </Typography>
                                      </Box>
                                    ) : (
                                      <TextField
                                        size="small"
                                        type="date"
                                        label="Joining date"
                                        InputLabelProps={{ shrink: true }}
                                        value={joiningDateByCandidate[cid] || ''}
                                        onChange={(e) =>
                                          setJoiningDateByCandidate((p) => ({ ...p, [cid]: e.target.value }))
                                        }
                                        sx={{ minWidth: 200 }}
                                      />
                                    )}
                                    <Button
                                      variant="contained"
                                      color="secondary"
                                      onClick={() => sendLoi(cid)}
                                      disabled={!['provisional_accepted'].includes(c.status)}
                                    >
                                      Send LOI
                                    </Button>
                                  </Stack>
                                </Stack>
                              );
                            })()}
                          </CardContent>
                        </Card>

                        <Card variant="outlined" sx={{ borderRadius: 2 }}>
                          <CardHeader title="5 · Timeline" />
                          <CardContent>
                            {timelineByCandidate[cid] ? (
                              <Typography variant="body2" color="text.secondary">
                                {timelineSummary(timelineByCandidate[cid])}
                              </Typography>
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                Loading timeline…
                              </Typography>
                            )}
                          </CardContent>
                        </Card>
                      </Stack>
                    );
                  })()
                ) : (
                  <Stack spacing={2} sx={{ minHeight: 0 }}>
                    <Stack
                      direction={{ xs: 'column', sm: 'row' }}
                      spacing={1.5}
                      alignItems={{ sm: 'center' }}
                      justifyContent="space-between"
                    >
                      <Box>
                        <Typography variant="h6" fontWeight={800}>
                          Selection candidates
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {filteredSelectionCandidates.length} of {selectionCandidates.length} shown · click a row to manage onboarding
                        </Typography>
                      </Box>
                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        <Button size="small" variant="outlined" startIcon={<LinkIcon />} onClick={fetchStaffingSelfRegistrationLink}>
                          Registration link
                        </Button>
                        <Button
                          size="small"
                          variant={addCandidatesOpen ? 'contained' : 'outlined'}
                          onClick={() => setAddCandidatesOpen((o) => !o)}
                          endIcon={
                            <ExpandMoreIcon sx={{ transform: addCandidatesOpen ? 'rotate(180deg)' : 'none', transition: '0.2s' }} />
                          }
                        >
                          Add candidates
                        </Button>
                      </Stack>
                    </Stack>

                    <TextField
                      size="small"
                      fullWidth
                      placeholder="Search by name, mobile, email, or status…"
                      value={selectionSearch}
                      onChange={(e) => setSelectionSearch(e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon fontSize="small" color="action" />
                          </InputAdornment>
                        )
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#fff' }
                      }}
                    />

                    <Box
                      sx={{
                        flex: 1,
                        minHeight: 280,
                        maxHeight: { xs: '55vh', md: 'calc(100vh - 260px)' },
                        overflowY: 'auto',
                        pr: 0.5
                      }}
                    >
                      {selectionCandidates.length === 0 ? (
                        <Card variant="outlined" sx={{ borderRadius: 2, textAlign: 'center', py: 4 }}>
                          <Typography color="text.secondary" gutterBottom>
                            No candidates yet.
                          </Typography>
                          <Button size="small" variant="contained" onClick={() => setAddCandidatesOpen(true)} sx={{ mt: 1 }}>
                            Add your first candidate
                          </Button>
                        </Card>
                      ) : filteredSelectionCandidates.length === 0 ? (
                        <Typography color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
                          No candidates match &ldquo;{selectionSearch}&rdquo;
                        </Typography>
                      ) : (
                        <Stack spacing={1}>
                          {filteredSelectionCandidates.map((c) => {
                            const statusColor =
                              c.status === 'loi_accepted' || c.status === 'active'
                                ? 'success'
                                : c.status === 'loi_sent' || c.status === 'provisional_sent'
                                  ? 'warning'
                                  : 'default';
                            return (
                              <Card
                                key={c.id}
                                variant="outlined"
                                sx={{
                                  borderRadius: 2,
                                  borderColor: '#e2e8f0',
                                  '&:hover': { borderColor: '#0d9488', boxShadow: '0 2px 12px rgba(13,148,136,0.12)' }
                                }}
                              >
                                <CardActionArea onClick={() => setSelectionDetailId(c.id)}>
                                  <CardContent sx={{ py: 1.25, '&:last-child': { pb: 1.25 } }}>
                                    <Stack direction="row" alignItems="center" spacing={1.5} sx={{ minWidth: 0 }}>
                                      <Avatar sx={{ bgcolor: '#0d9488', width: 40, height: 40, fontSize: '1rem' }}>
                                        {(c.full_name || '?').charAt(0).toUpperCase()}
                                      </Avatar>
                                      <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
                                          <Typography fontWeight={700} noWrap sx={{ flex: 1 }}>
                                            {c.full_name}
                                          </Typography>
                                          <ChevronRightIcon sx={{ color: 'text.disabled', flexShrink: 0 }} />
                                        </Stack>
                                        <Typography variant="caption" color="text.secondary" noWrap display="block">
                                          {[c.mobile, c.email].filter(Boolean).join(' · ') || 'No contact on file'}
                                        </Typography>
                                        <Stack direction="row" spacing={0.5} sx={{ mt: 0.75 }} flexWrap="wrap" useFlexGap>
                                          <Chip size="small" color={statusColor} label={formatCandidateStatus(c.status)} />
                                          {c.profile_completed_at ? (
                                            <Chip size="small" color="success" variant="outlined" label="Profile done" />
                                          ) : c.profile_invite_sent ? (
                                            <Chip size="small" color="warning" variant="outlined" label="Invite sent" />
                                          ) : null}
                                          {c.joining_date && (
                                            <Chip size="small" variant="outlined" label={`Join ${String(c.joining_date).slice(0, 10)}`} />
                                          )}
                                        </Stack>
                                      </Box>
                                    </Stack>
                                  </CardContent>
                                </CardActionArea>
                              </Card>
                            );
                          })}
                        </Stack>
                      )}
                    </Box>

                    <Collapse in={addCandidatesOpen}>
                      <Card variant="outlined" sx={{ borderRadius: 2, bgcolor: '#f8fafc' }}>
                        <CardContent>
                          <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                            Add candidates manually
                          </Typography>
                          <Grid container spacing={1.5} sx={{ mb: 2 }}>
                            <Grid item xs={12} sm={6} md={3}>
                              <TextField
                                size="small"
                                fullWidth
                                label="Name"
                                value={candidateForm.name}
                                onChange={(e) => setCandidateForm((p) => ({ ...p, name: e.target.value }))}
                              />
                            </Grid>
                            <Grid item xs={6} sm={3} md={2}>
                              <TextField
                                size="small"
                                fullWidth
                                label="Mobile"
                                value={candidateForm.mobile}
                                onChange={(e) => setCandidateForm((p) => ({ ...p, mobile: e.target.value }))}
                              />
                            </Grid>
                            <Grid item xs={6} sm={3} md={3}>
                              <TextField
                                size="small"
                                fullWidth
                                label="Email"
                                value={candidateForm.email}
                                onChange={(e) => setCandidateForm((p) => ({ ...p, email: e.target.value }))}
                              />
                            </Grid>
                            <Grid item xs={12} sm={6} md={2}>
                              <TextField
                                size="small"
                                fullWidth
                                label="Qualification"
                                value={candidateForm.qualification}
                                onChange={(e) => setCandidateForm((p) => ({ ...p, qualification: e.target.value }))}
                              />
                            </Grid>
                            <Grid item xs={12} sm={6} md={2}>
                              <Button variant="contained" fullWidth onClick={addSingleCandidate} sx={{ height: 40 }}>
                                Add
                              </Button>
                            </Grid>
                          </Grid>
                          <Divider sx={{ my: 1.5 }} />
                          <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" sx={{ mb: 1 }}>
                            Or bulk upload CSV
                          </Typography>
                          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
                            <Button size="small" onClick={downloadBulkTemplate}>
                              Template
                            </Button>
                            <Button size="small" variant="outlined" component="label">
                              Choose file
                              <input type="file" hidden accept=".csv" onChange={(e) => setBulkFile(e.target.files?.[0] || null)} />
                            </Button>
                            <Typography variant="caption" color="text.secondary">
                              {bulkFile?.name || 'No file'}
                            </Typography>
                            <Button size="small" variant="contained" onClick={uploadBulk} disabled={!bulkFile}>
                              Upload CSV
                            </Button>
                          </Stack>
                        </CardContent>
                      </Card>
                    </Collapse>
                  </Stack>
                ))}

              {activeTab === 'leavePolicies' && (
                <StaffingLeavePoliciesTab
                  apiRequest={apiRequest}
                  token={token}
                  activeEmployeesList={activeEmployeesList}
                  onFeedback={setFeedback}
                />
              )}

              {activeTab === 'hrPolicy' && (
                <StaffingHrPolicyTab apiRequest={apiRequest} token={token} onFeedback={setFeedback} />
              )}

              {activeTab === 'grievances' && (
                <StaffingGrievancesTab apiRequest={apiRequest} onFeedback={setFeedback} />
              )}

              {activeTab === 'leaveRequests' && (
                <Stack spacing={2}>
                  <Typography variant="h6" fontWeight={700}>Leave &amp; outdoor duty requests</Typography>
                  <TextField select size="small" label="Status" value={requestStatusFilter} sx={{ maxWidth: 200 }}
                    onChange={(e) => setRequestStatusFilter(e.target.value)}>
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="pending">Pending</MenuItem>
                    <MenuItem value="approved">Approved</MenuItem>
                    <MenuItem value="rejected">Rejected</MenuItem>
                  </TextField>
                  <Typography variant="subtitle1" fontWeight={700}>Leave requests</Typography>
                  {leaveRequests.length === 0 ? (
                    <Typography color="text.secondary">No leave requests.</Typography>
                  ) : (
                    leaveRequests.map((r) => (
                      <Card key={r.id} variant="outlined">
                        <CardContent sx={{ py: 1.5 }}>
                          <Stack direction="row" justifyContent="space-between" flexWrap="wrap" useFlexGap>
                            <Box>
                              <Typography fontWeight={700}>{r.employee_name}</Typography>
                              <Typography variant="body2">
                                {r.leave_type} · {r.from_date} to {r.to_date} ({r.days}d) — <strong>{r.status}</strong>
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Manager: {r.manager_name || '—'} · {r.reason || 'No reason'}
                              </Typography>
                            </Box>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Chip size="small" label={r.status} color={r.status === 'approved' ? 'success' : r.status === 'rejected' ? 'error' : 'warning'} />
                              {r.status === 'pending' && r.action_token && (
                                <Button size="small" variant="outlined" onClick={() => copyReviewLink('/staffing-leave-review', r.action_token)}>
                                  Copy review link
                                </Button>
                              )}
                            </Stack>
                          </Stack>
                        </CardContent>
                      </Card>
                    ))
                  )}
                  <Typography variant="subtitle1" fontWeight={700} sx={{ mt: 2 }}>Outdoor duty</Typography>
                  {outdoorRequests.length === 0 ? (
                    <Typography color="text.secondary">No outdoor duty requests.</Typography>
                  ) : (
                    outdoorRequests.map((r) => (
                      <Card key={r.id} variant="outlined">
                        <CardContent sx={{ py: 1.5 }}>
                          <Typography fontWeight={700}>{r.employee_name}</Typography>
                          <Typography variant="body2">
                            {r.from_date} to {r.to_date} — <strong>{r.status}</strong>
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {r.location || '—'} · {r.purpose || '—'}
                          </Typography>
                          {r.status === 'pending' && r.action_token && (
                            <Button size="small" sx={{ mt: 1 }} variant="outlined" onClick={() => copyReviewLink('/staffing-outdoor-review', r.action_token)}>
                              Copy review link
                            </Button>
                          )}
                        </CardContent>
                      </Card>
                    ))
                  )}
                </Stack>
              )}

              {activeTab === 'attendance' && (
                <Stack spacing={2}>
                  <Typography variant="h6" fontWeight={700}>Attendance</Typography>
                  <Alert severity="info">
                    Candidates check in/out via the mobile app. Download that data below, or upload a monthly CSV to override payroll.
                    Upload matching uses <strong>mobile number only</strong> (10 digits). Public holidays count as paid days even without check-in.
                  </Alert>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <TextField size="small" select label="Month" value={attendanceMonth} onChange={(e) => setAttendanceMonth(Number(e.target.value))} sx={{ minWidth: 120 }}>
                      {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                        <MenuItem key={m} value={m}>{m}</MenuItem>
                      ))}
                    </TextField>
                    <TextField size="small" type="number" label="Year" value={attendanceYear} onChange={(e) => setAttendanceYear(Number(e.target.value))} />
                    <Button variant="contained" onClick={loadAttendance}>Refresh</Button>
                  </Stack>
                  <Card variant="outlined" sx={{ bgcolor: '#f0f9ff' }}>
                    <CardContent>
                      <Typography fontWeight={700} gutterBottom>Download app attendance</Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                        Export check-ins/check-outs posted by employees in the candidate app for the selected month.
                      </Typography>
                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        <Button variant="contained" startIcon={<DownloadIcon />} onClick={() => downloadAppAttendance('detail')} sx={{ bgcolor: '#0d9488' }}>
                          All records (detail)
                        </Button>
                        <Button variant="outlined" startIcon={<DownloadIcon />} onClick={() => downloadAppAttendance('summary')}>
                          Daily summary
                        </Button>
                      </Stack>
                    </CardContent>
                  </Card>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography fontWeight={700} gutterBottom>Upload attendance sheet (CSV)</Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                        Summary: <strong>mobile</strong> + present_days. Daily: mobile, date, status (P = present).
                      </Typography>
                      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ md: 'center' }} flexWrap="wrap" useFlexGap>
                        <Button variant="text" onClick={downloadAttendanceTemplate}>Download upload template</Button>
                        <Button variant="outlined" component="label">
                          Choose CSV
                          <input type="file" hidden accept=".csv" onChange={(e) => setAttendanceBulkFile(e.target.files?.[0] || null)} />
                        </Button>
                        <Typography variant="body2" color="text.secondary">{attendanceBulkFile?.name || 'No file selected'}</Typography>
                        <Button variant="contained" onClick={uploadAttendanceSheet} disabled={!attendanceBulkFile}>
                          Upload for {String(attendanceMonth).padStart(2, '0')}/{attendanceYear}
                        </Button>
                      </Stack>
                    </CardContent>
                  </Card>
                  {attendanceRows.length === 0 ? (
                    <Typography color="text.secondary">No active employees.</Typography>
                  ) : (
                    attendanceRows.map((row) => (
                      <Card key={row.id} variant="outlined">
                        <CardContent sx={{ py: 1.5 }}>
                          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1}>
                            <Box>
                              <Typography fontWeight={700}>{row.full_name}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                Mobile: {row.mobile || '—'}
                              </Typography>
                              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                                <Chip size="small" variant="outlined" label={`App check-ins: ${row.app_checkin_days ?? 0}`} />
                                {row.uploaded_present_days != null ? (
                                  <Chip size="small" color="primary" label={`Sheet: ${row.uploaded_present_days} days`} />
                                ) : (
                                  <Chip size="small" variant="outlined" label="No sheet uploaded" />
                                )}
                                <Chip
                                  size="small"
                                  color={row.attendance_source === 'company_upload' ? 'success' : 'default'}
                                  label={`Payroll: ${row.payroll_paid_days} days (${row.attendance_source === 'company_upload' ? 'from sheet' : 'from app'})`}
                                />
                              </Stack>
                              {row.checkins?.length > 0 && row.attendance_source !== 'company_upload' && (
                                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                                  Check-in dates: {row.checkins.map((c) => String(c.day).slice(0, 10)).join(', ')}
                                </Typography>
                              )}
                            </Box>
                            {row.uploaded_present_days != null && (
                              <Button size="small" color="warning" variant="outlined" onClick={() => clearUploadedAttendance(row.id)}>
                                Clear sheet
                              </Button>
                            )}
                          </Stack>
                        </CardContent>
                      </Card>
                    ))
                  )}
                </Stack>
              )}

              {activeTab === 'publicHolidays' && (
                <StaffingPublicHolidaysTab apiRequest={apiRequest} onFeedback={setFeedback} />
              )}

              {activeTab === 'salaryStructure' && (
                <StaffingSalaryStructureTab
                  apiRequest={apiRequest}
                  activeEmployeesList={activeEmployeesList}
                  onFeedback={setFeedback}
                />
              )}

              {activeTab === 'documents' && (
                <Stack spacing={2}>
                  <Typography variant="h6" fontWeight={700}>Documents</Typography>

                  <Card variant="outlined" sx={{ borderRadius: 2, bgcolor: '#f0fdfa', borderColor: '#99f6e4' }}>
                    <CardContent>
                      <Typography fontWeight={700} gutterBottom>
                        Salary slips (bulk download)
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Download all employee payslip PDFs for a payroll month as one ZIP file. Payroll must be processed for that month first.
                      </Typography>
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }} flexWrap="wrap" useFlexGap>
                        <TextField
                          size="small"
                          select
                          label="Month"
                          value={payslipZipMonth}
                          onChange={(e) => setPayslipZipMonth(Number(e.target.value))}
                          sx={{ minWidth: 120 }}
                        >
                          {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                            <MenuItem key={m} value={m}>
                              {new Date(2000, m - 1, 1).toLocaleString(undefined, { month: 'long' })}
                            </MenuItem>
                          ))}
                        </TextField>
                        <TextField
                          size="small"
                          type="number"
                          label="Year"
                          value={payslipZipYear}
                          onChange={(e) => setPayslipZipYear(Number(e.target.value))}
                          sx={{ width: 100 }}
                        />
                        <Button
                          variant="contained"
                          startIcon={<DownloadIcon />}
                          onClick={downloadPayslipsZipByMonth}
                          disabled={payslipZipLoading}
                          sx={{ bgcolor: '#0d9488' }}
                        >
                          {payslipZipLoading ? 'Preparing ZIP…' : 'Download payslips ZIP'}
                        </Button>
                      </Stack>
                    </CardContent>
                  </Card>

                  <Divider />
                  <Typography variant="subtitle1" fontWeight={700}>
                    Employee documents (matched by mobile)
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Contracts, stipends, and salary slips from your company uploads are linked to employees using their{' '}
                    <strong>10-digit mobile number</strong>, not employee code.
                  </Typography>
                  <Button size="small" variant="outlined" sx={{ mb: 1.5 }} onClick={syncDocumentsByMobile}>
                    Sync all employee documents by mobile
                  </Button>
                  <StaffingEmployeeSelect
                    employees={employees}
                    value={documentsCandidateId}
                    onChange={(id) => {
                      setDocumentsCandidateId(id);
                      loadEmployeeDocuments(id);
                    }}
                    showStatus
                    sx={{ maxWidth: { xs: '100%', sm: 520 } }}
                  />
                  {documentsData?.candidate?.mobile && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Mobile key: <strong>{documentsData.match_key || documentsData.candidate.mobile}</strong>
                      {documentsData.documents_linked_by_mobile > 0 && (
                        <> · {documentsData.documents_linked_by_mobile} document(s) newly linked</>
                      )}
                    </Typography>
                  )}
                  {documentsData?.company_documents?.length > 0 && (
                    <Stack spacing={1} sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" fontWeight={700}>Company documents (by mobile)</Typography>
                      {documentsData.company_documents.map((doc) => (
                        <Card key={doc.id} variant="outlined">
                          <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
                            <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" useFlexGap>
                              <Box>
                                <Typography variant="body2" fontWeight={600}>
                                  {doc.document_type} {doc.issue_date ? `· ${doc.issue_date}` : ''}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {doc.candidate_name || '—'} · {doc.linked ? 'Linked to employee' : 'Unlinked'}
                                </Typography>
                              </Box>
                              <Button
                                size="small"
                                variant="outlined"
                                href={
                                  doc.document_type === 'contract'
                                    ? `${API_URL}/api/documents/contract/${doc.id}/pdf?token=${token}`
                                    : `${API_URL}/api/documents/${doc.id}/pdf?token=${token}`
                                }
                                target="_blank"
                                rel="noreferrer"
                              >
                                View PDF
                              </Button>
                            </Stack>
                          </CardContent>
                        </Card>
                      ))}
                    </Stack>
                  )}
                  {documentsData?.profile_data?.files && (
                    <Stack spacing={1}>
                      <Typography variant="subtitle2" fontWeight={700}>Profile uploads</Typography>
                      {documentsData.profile_data.files.resume && (
                        <Button size="small" href={`${API_URL}${documentsData.profile_data.files.resume}`} target="_blank" rel="noreferrer">Resume</Button>
                      )}
                      {documentsData.profile_data.files.education_proof && (
                        <Button size="small" href={`${API_URL}${documentsData.profile_data.files.education_proof}`} target="_blank" rel="noreferrer">Education proof</Button>
                      )}
                      {(documentsData.profile_data.files.other_documents || []).map((p, i) => (
                        <Button key={i} size="small" href={`${API_URL}${p}`} target="_blank" rel="noreferrer">Document {i + 1}</Button>
                      ))}
                    </Stack>
                  )}
                  {documentsCandidateId &&
                    !documentsData?.profile_data?.files &&
                    !(documentsData?.company_documents?.length > 0) && (
                    <Typography color="text.secondary">No documents found for this employee&apos;s mobile.</Typography>
                  )}
                </Stack>
              )}

              {activeTab === 'payroll' && (
                <Stack spacing={2}>
                  <Alert severity="info">
                    Paid days = check-ins and/or uploaded attendance, plus <strong>public holidays</strong> (see Public Holidays tab).
                    Set up roles and employee salary in the <strong>Salary Structure</strong> tab first.
                    Pick a month to preview, then <strong>Process payroll</strong> and <strong>Publish to mobile app</strong> when ready.
                    <Button size="small" sx={{ ml: 1, mt: { xs: 1, sm: 0 } }} onClick={() => setActiveTab('salaryStructure')}>
                      Go to Salary Structure
                    </Button>
                  </Alert>

                  <Card variant="outlined">
                    <CardContent>
                      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ md: 'center' }} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
                        <Typography variant="h6" fontWeight={700} sx={{ flex: 1 }}>
                          {payrollPreview?.period_label || `${String(payrollMonth).padStart(2, '0')}/${payrollYear}`}
                        </Typography>
                        <TextField size="small" select label="Month" value={payrollMonth} onChange={(e) => setPayrollMonth(Number(e.target.value))} sx={{ minWidth: 120 }}>
                          {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                            <MenuItem key={m} value={m}>{m}</MenuItem>
                          ))}
                        </TextField>
                        <TextField size="small" label="Year" type="number" value={payrollYear} onChange={(e) => setPayrollYear(Number(e.target.value))} sx={{ width: 100 }} />
                        <Button size="small" variant="outlined" onClick={loadPayrollPreview} disabled={payrollPreviewLoading}>
                          Refresh preview
                        </Button>
                      </Stack>

                      {payrollPreviewLoading && <LinearProgress sx={{ mb: 2 }} />}

                      {(payrollPreview?.warnings || []).length > 0 && (
                        <Alert severity="warning" sx={{ mb: 2 }}>
                          {(payrollPreview.warnings || []).map((w, i) => (
                            <Typography key={i} variant="body2">{w}</Typography>
                          ))}
                        </Alert>
                      )}

                      {payrollPreview?.payroll_run && (
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
                          <Chip
                            size="small"
                            label={payrollPreview.payroll_run.status === 'processed' ? 'Processed' : payrollPreview.payroll_run.status}
                            color="primary"
                            variant="outlined"
                          />
                          {payrollPreview.payroll_run.processed_at && (
                            <Chip
                              size="small"
                              variant="outlined"
                              label={`Processed ${new Date(payrollPreview.payroll_run.processed_at).toLocaleString()}`}
                            />
                          )}
                          {payrollPreview.payroll_run.app_published_at ? (
                            <Chip size="small" color="success" label={`Published to app ${new Date(payrollPreview.payroll_run.app_published_at).toLocaleString()}`} />
                          ) : (
                            <Chip size="small" color="warning" variant="outlined" label="Not published to app yet" />
                          )}
                        </Stack>
                      )}

                      <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Employee</TableCell>
                              <TableCell align="right">Paid days</TableCell>
                              <TableCell align="right">LWP</TableCell>
                              <TableCell align="right">Gross</TableCell>
                              <TableCell align="right">Deductions</TableCell>
                              <TableCell align="right">Net pay</TableCell>
                              <TableCell>Attendance</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {(payrollPreview?.employees || []).map((row) => (
                              <TableRow key={row.candidate_id} hover>
                                <TableCell>
                                  <Typography variant="body2" fontWeight={600}>{row.full_name}</Typography>
                                  {row.employee_code && (
                                    <Typography variant="caption" color="text.secondary">{row.employee_code}</Typography>
                                  )}
                                </TableCell>
                                <TableCell align="right">{row.paid_days}</TableCell>
                                <TableCell align="right">{row.lwp_days}</TableCell>
                                <TableCell align="right">₹{Number(row.gross_earnings || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</TableCell>
                                <TableCell align="right">₹{Number(row.total_deductions || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</TableCell>
                                <TableCell align="right">
                                  <Typography fontWeight={600}>₹{Number(row.net_pay || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</Typography>
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    size="small"
                                    variant="outlined"
                                    label={row.attendance_source === 'company_upload' ? 'Sheet' : 'App'}
                                  />
                                </TableCell>
                              </TableRow>
                            ))}
                            {(payrollPreview?.employees || []).length === 0 && !payrollPreviewLoading && (
                              <TableRow>
                                <TableCell colSpan={7}>
                                  <Typography color="text.secondary">No active employees for this month.</Typography>
                                </TableCell>
                              </TableRow>
                            )}
                            {(payrollPreview?.totals?.employee_count || 0) > 0 && (
                              <TableRow sx={{ bgcolor: 'action.hover' }}>
                                <TableCell><Typography fontWeight={700}>Total ({payrollPreview.totals.employee_count})</Typography></TableCell>
                                <TableCell colSpan={2} />
                                <TableCell align="right">
                                  <Typography fontWeight={700}>₹{Number(payrollPreview.totals.gross_earnings || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</Typography>
                                </TableCell>
                                <TableCell align="right">
                                  <Typography fontWeight={700}>₹{Number(payrollPreview.totals.total_deductions || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</Typography>
                                </TableCell>
                                <TableCell align="right">
                                  <Typography fontWeight={700}>₹{Number(payrollPreview.totals.net_pay || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</Typography>
                                </TableCell>
                                <TableCell />
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>

                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} flexWrap="wrap" useFlexGap>
                        <Button
                          variant="contained"
                          disabled={payrollActionLoading || !(payrollPreview?.employees || []).length}
                          onClick={() => runPayroll(false)}
                        >
                          Process payroll
                        </Button>
                        <Button
                          variant="contained"
                          color="success"
                          disabled={
                            payrollActionLoading ||
                            !payrollPreview?.payroll_run?.id ||
                            !!payrollPreview?.payroll_run?.app_published_at
                          }
                          onClick={publishPayrollToApp}
                        >
                          Publish to mobile app
                        </Button>
                        {payrollPreview?.payroll_run?.id && (
                          <Button
                            variant="outlined"
                            startIcon={<DownloadIcon />}
                            onClick={() =>
                              downloadPayslipsZip({
                                id: payrollPreview.payroll_run.id,
                                month: payrollMonth,
                                year: payrollYear
                              })
                            }
                          >
                            Download ZIP
                          </Button>
                        )}
                        <Button size="small" variant="text" color="inherit" onClick={() => distributePayslips(payrollPreview?.payroll_run?.id)} disabled={!payrollPreview?.payroll_run?.id}>
                          Email payslips (optional)
                        </Button>
                      </Stack>
                    </CardContent>
                  </Card>

                  {payrollRuns.length > 0 && (
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Past payroll runs</Typography>
                        <Stack spacing={1}>
                          {payrollRuns.slice(0, 6).map((r) => (
                            <Stack key={r.id} direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between" alignItems={{ sm: 'center' }}>
                              <Box>
                                <Typography variant="body2" fontWeight={600}>{`${String(r.month).padStart(2, '0')}/${r.year}`}</Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {r.app_published_at ? `Published ${new Date(r.app_published_at).toLocaleDateString()}` : 'Not published'}
                                  {r.payslips_distributed_at ? ` · Emailed ${new Date(r.payslips_distributed_at).toLocaleDateString()}` : ''}
                                </Typography>
                              </Box>
                              <Stack direction="row" spacing={1}>
                                <Button size="small" variant="outlined" onClick={() => { setPayrollMonth(r.month); setPayrollYear(r.year); }}>Open</Button>
                                <Button size="small" variant="outlined" onClick={() => downloadPayslipsZip(r)}>ZIP</Button>
                              </Stack>
                            </Stack>
                          ))}
                        </Stack>
                      </CardContent>
                    </Card>
                  )}
                </Stack>
              )}
            </Container>
          </Box>
        </Box>
      </Box>

      <Dialog
        open={inviteLinkDialog.open}
        onClose={() => setInviteLinkDialog((p) => ({ ...p, open: false }))}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Profile completion link</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              Share this link with the candidate. When they open it, they can fill in their details and upload documents — same
              idea as the supervisor candidate registration link.
            </Typography>
            {inviteLinkDialog.emailSent && (
              <Alert severity="success">An email was sent to the candidate with this same link.</Alert>
            )}
            {inviteLinkDialog.emailSkipped && (
              <Alert severity="warning">
                This candidate has no email on file. Copy the link below and send it via WhatsApp, SMS, or another channel.
              </Alert>
            )}
            {!inviteLinkDialog.emailSkipped && inviteLinkDialog.emailError && (
              <Alert severity="warning">Email could not be sent: {inviteLinkDialog.emailError}. You can still share the link below.</Alert>
            )}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="stretch">
              <TextField
                fullWidth
                size="small"
                label="Link"
                value={inviteLinkDialog.profileUrl}
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton edge="end" onClick={copyInviteLinkToClipboard} aria-label="Copy link">
                        <ContentCopyIcon />
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
              <Button
                variant="contained"
                color="success"
                onClick={copyInviteLinkToClipboard}
                sx={{ minWidth: { sm: 120 }, whiteSpace: 'nowrap' }}
                startIcon={<ContentCopyIcon />}
              >
                Copy
              </Button>
            </Stack>
            <Alert severity="success" variant="outlined" sx={{ bgcolor: '#f0fdf4', borderColor: '#86efac' }}>
              <Typography variant="caption" fontWeight={700} color="#166534" component="p" sx={{ m: 0 }}>
                Tip: Share this link via WhatsApp, Email, or SMS. The address uses this website so it matches where you are
                logged in (like the supervisor registration link).
              </Typography>
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInviteLinkDialog((p) => ({ ...p, open: false }))}>Done</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={selfRegDialog.open}
        onClose={() => setSelfRegDialog((p) => ({ ...p, open: false }))}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Candidate self-registration link</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              Anyone with this link can submit their details and join your <strong>selection candidates</strong> list. Share
              only with people you intend to recruit. Copy the URL and send it via WhatsApp, SMS, or email.
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="stretch">
              <TextField
                fullWidth
                size="small"
                label="Registration URL"
                value={selfRegDialog.url}
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton edge="end" onClick={copySelfRegLinkToClipboard} aria-label="Copy registration link">
                        <ContentCopyIcon />
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
              <Button
                variant="contained"
                color="success"
                onClick={copySelfRegLinkToClipboard}
                sx={{ minWidth: { sm: 120 }, whiteSpace: 'nowrap' }}
                startIcon={<ContentCopyIcon />}
              >
                Copy
              </Button>
            </Stack>
            <Alert severity="success" variant="outlined" sx={{ bgcolor: '#f0fdf4', borderColor: '#86efac' }}>
              <Typography variant="caption" fontWeight={700} color="#166534" component="p" sx={{ m: 0 }}>
                Tip: Share via WhatsApp, SMS, or email — same workflow as the supervisor &quot;Candidate registration link&quot;.
              </Typography>
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelfRegDialog((p) => ({ ...p, open: false }))}>Done</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default StaffingCompanyDashboard;
